import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import * as XLSX from 'xlsx';
import { ToastProvider, useToast } from '../components/Toast';
import { useColumnCustomizer } from '../components/ColumnCustomizer';
import { usePermissions } from '../hooks/usePermissions';
import { applySort, makeSortToggle, SortableTh } from '../components/SortableTable';
import ChipOverflowStrip from '../components/ChipOverflowStrip';
import PillTabs from '../components/PillTabs';

// Every fetch in this file must go through BASE, never a raw literal — a
// hardcoded literal URL works in local dev but is unreachable
// from a real visitor's browser in production, silently failing.
const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// "1st", "2nd", "3rd", "11th" ... — used for the recurring-task Creation/Due Date pickers
function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// Month-offset option sets for the recurring-schedule pickers, both expressed as an offset
// from April (FY start) — this is the exact scale the backend's FY-anchored trigger formula
// (ca-backend/utils/recurringTaskGenerator.js) uses, so no conversion is needed on save.
const QUARTER_MONTH_OPTIONS = [{ v: 0, l: 'First' }, { v: 1, l: 'Second' }, { v: 2, l: 'Third' }];
const HALF_YEAR_MONTH_OPTIONS = [
  { v: 0, l: 'Apr & Oct' }, { v: 1, l: 'May & Nov' }, { v: 2, l: 'Jun & Dec' },
  { v: 3, l: 'Jul & Jan' }, { v: 4, l: 'Aug & Feb' }, { v: 5, l: 'Sep & Mar' },
];
// Yearly frequency picks one exact month (FY-anchored, April=0…March=11 — same scale the
// backend's FY-anchored trigger formula in recurringTaskGenerator.js already uses, so no
// conversion is needed on save) via a Day + Month dropdown pair, same visual pattern as every
// other frequency — a native <input type="date"> would have forced a meaningless scratch year
// (e.g. "2000") into view, which is what made the old picker confusing.
const YEARLY_MONTH_OPTIONS = [
  { v: 0, l: 'April' }, { v: 1, l: 'May' }, { v: 2, l: 'June' }, { v: 3, l: 'July' },
  { v: 4, l: 'August' }, { v: 5, l: 'September' }, { v: 6, l: 'October' }, { v: 7, l: 'November' },
  { v: 8, l: 'December' }, { v: 9, l: 'January' }, { v: 10, l: 'February' }, { v: 11, l: 'March' },
];

const CATEGORIES = [
  'All Categories',
  'GST & Indirect Tax',
  'Income Tax & TDS',
  'Company Law (ROC/MCA)',
  'Audit & Assurance',
  'Accounting & Bookkeeping',
  'Registration & Licenses',
  'Payroll & HR',
  'Certification',
  'Other'
];

const SERVICE_COLUMNS = [
  { key: 'service_code', label: 'Service ID' },
  { key: 'name',         label: 'Service Name' },
  { key: 'category',     label: 'Category' },
  { key: 'service_type', label: 'Type' },
  { key: 'gst_percent',  label: 'GST %', sortType: 'number', get: s => (s.gst_percent == null || s.gst_percent === '' ? 18 : s.gst_percent) },
  { key: 'default_amount', label: 'Default Rate', sortType: 'number' },
  { key: 'billing_frequency', label: 'Recurring' },
  { key: 'status',       label: 'Status' },
];

// Services action permissions (Settings → Manage Access → Services). The sidebar only checks
// "view"; every Create / Edit / Delete / Export control inside this module goes through this.
// Flags stay false until the role's permissions have loaded (so action buttons never flash for
// a user who lacks them); admin is always full. Buttons are HIDDEN (not disabled) when a
// permission is missing; deny() is the belt-and-braces guard for a handler that fires anyway
// (e.g. an already-open modal after an admin revokes access) — it toasts and the caller skips
// the API. Backend enforcement is a separate task (RBAC Phase 2). Mirrors useCrmAccess in CRM.js.
function useServiceAccess(showToast) {
  const { can, loading, role } = usePermissions();
  const ready = role === 'admin' || !loading;
  const flags = {
    canCreate: ready && can('services', 'create'),
    canEdit:   ready && can('services', 'edit'),
    canDelete: ready && can('services', 'delete'),
    canExport: ready && can('services', 'export'),
  };
  const deny = (action) => {
    showToast(`You don't have permission to ${action} services. Ask your admin.`, 'error');
    return false;
  };
  // Cross-module check for actions that touch another module's data
  // (Run recurring generation → tasks.create, Retainer Generate Invoice → finance.create).
  const canIn = (module, action) => ready && can(module, action);
  return { ...flags, deny, canIn };
}

function ConfirmModal({ open, title, message, onConfirm, onCancel, confirmLabel='Confirm', confirmColor='#ef4444' }) {
  if (!open) return null;
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:99999, padding:16 }}>
      <div style={{ background:'#fff', borderRadius:16, width:'min(92vw,420px)', overflow:'hidden', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding:'20px 24px', borderBottom:'1px solid #f1f5f9' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
            <div style={{ width:36, height:36, borderRadius:10, background:'#fef2f2', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
            </div>
            <h3 style={{ fontSize:15, fontWeight:700, color:'#0f1f45', margin:0 }}>{title}</h3>
          </div>
          <p style={{ fontSize:13, color:'#64748b', margin:0, lineHeight:1.6 }}>{message}</p>
        </div>
        <div style={{ display:'flex', gap:10, padding:'16px 24px' }}>
          <button onClick={onCancel} style={{ flex:1, padding:'9px 0', borderRadius:9, border:'1px solid #e2e8f0', background:'#f8fafc', color:'#4a5568', fontSize:13, fontWeight:600, cursor:'pointer' }}>Cancel</button>
          <button onClick={onConfirm} style={{ flex:1, padding:'9px 0', borderRadius:9, border:'none', background:confirmColor, color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

function ServicesBody({ darkMode = false, T: theme }) {
  const { showToast } = useToast();
  const { canCreate, canEdit, canDelete, canExport, deny } = useServiceAccess(showToast);
  const [confirm, setConfirm] = useState(null);
  const showConfirm = (title, message, onConfirm, confirmLabel='Confirm', confirmColor='#ef4444') => setConfirm({ title, message, onConfirm, confirmLabel, confirmColor });
  const [activeSubTab, setActiveSubTab] = useState('services');
  const T = theme || { bg:'#f1f5f9', bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('All Categories');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [openKebabId, setOpenKebabId] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [catList, setCatList] = useState([]); // service_categories from Settings; empty => fall back to defaults

  const fetchCategories = () => {
    fetch(`${BASE}/api/service-categories`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setCatList(Array.isArray(d) ? d : [])).catch(() => {});
  };
  useEffect(() => { fetchCategories(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const categoryNames = catList.length ? catList.map(c => c.name) : CATEGORIES.filter(c => c !== 'All Categories');

  const handleExport = () => {
    if (!canExport) return deny('export');
    const exportData = services.map(s => ({
      'Service ID': s.service_code || '',
      'Service Name': s.name,
      'Category': s.category,
      'Service Type': s.service_type,
      'Billing Frequency': s.billing_frequency || '',
      'Default Rate (₹)': s.default_amount || '',
      'GST %': s.gst_percent,
      'SAC Code': s.sac_code || '',
      'Description': s.description || '',
      'Invoice Description': s.invoice_description || '',
      'Status': s.status,
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    // Auto-width columns
    const colWidths = Object.keys(exportData[0] || {}).map(k => ({ wch: Math.max(k.length, 18) }));
    ws['!cols'] = colWidths;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Services');
    XLSX.writeFile(wb, 'ChanakyaX_Services.xlsx');
  };

  // Column customization — shared component, identical across every list module.
  const cc = useColumnCustomizer(SERVICE_COLUMNS, { visKey: 'svc_vis_cols', orderKey: 'svc_col_order', T, darkMode });
  const { visibleColumns, orderedVisibleKeys } = cc;
  const token = localStorage.getItem('token');

  const fetchServices = async () => {
    try {
      const res = await fetch(`${BASE}/api/services/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setServices(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchServices(); }, []);

  const handleDeleteService = async (serviceId, serviceName) => {
    setOpenKebabId(null);
    if (!canDelete) return deny('delete');
    showConfirm('Delete Service', `Delete service "${serviceName}"? This cannot be undone.`, async () => {
      try {
        const res = await fetch(`${BASE}/api/services/${serviceId}`, {
          method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) { showToast('Service deleted', 'success'); fetchServices(); }
        else { const d = await res.json().catch(() => ({})); showToast(d.message || 'Error deleting service', 'error'); }
      } catch (e) { showToast('Error deleting service', 'error'); }
    });
  };

  const [selectedIds, setSelectedIds] = useState([]);
  const lastClickedId = useRef(null);
  // Row checkboxes serve bulk delete (services.delete) and bulk ID assignment (services.edit).
  const selCol = canDelete || canEdit;
  // Click toggles one row; Shift+click selects the whole range between the last clicked row and
  // this one (in the currently displayed — sorted/filtered — order).
  const toggleSelect = (id, shiftKey = false) => {
    const order = sorted.map(s => s.id);
    if (shiftKey && lastClickedId.current != null && order.includes(lastClickedId.current)) {
      const a = order.indexOf(lastClickedId.current), b = order.indexOf(id);
      const range = order.slice(Math.min(a, b), Math.max(a, b) + 1);
      setSelectedIds(prev => Array.from(new Set([...prev, ...range])));
    } else {
      setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    }
    lastClickedId.current = id;
  };

  // Sorting + resizable headers: shared SortableTh (same as Clients / Finance / CRM).
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const onSort = makeSortToggle(sortKey, setSortKey, setSortDir);
  // Column widths dragged in the header are applied to the data cells too, so a narrowed
  // column actually clips its content instead of overlapping.
  const [, bumpColWidths] = useState(0);
  useEffect(() => {
    const h = (e) => { if (e.detail?.resizeId === 'services') bumpColWidths(n => n + 1); };
    window.addEventListener('colw-changed', h);
    return () => window.removeEventListener('colw-changed', h);
  }, []);
  const savedColWidth = (key) => {
    try { const v = Number(localStorage.getItem('colw:services:' + key)); return v >= 60 ? v : null; } catch { return null; }
  };

  // Tab badges (Services / Retainers / Templates) — the child tabs report their own counts
  // after every fetch so the badges stay in sync; these first fetches fill them before a tab opens.
  const [tabCounts, setTabCounts] = useState({ retainers: null, templates: null });
  useEffect(() => {
    const h = { 'Authorization': `Bearer ${token}` };
    fetch(`${BASE}/api/retainers/stats`, { headers: h }).then(r => r.json())
      .then(d => setTabCounts(c => ({ ...c, retainers: Number(d?.ongoing) || 0 }))).catch(() => {});
    fetch(`${BASE}/api/retainers/templates`, { headers: h }).then(r => r.json())
      .then(d => setTabCounts(c => ({ ...c, templates: Array.isArray(d) ? d.length : 0 }))).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // "New Retainer" / "New Template" live in the shared tab bar; bumping these opens the modal inside the child tab.
  const [newRetainerTick, setNewRetainerTick] = useState(0);
  const [newTemplateTick, setNewTemplateTick] = useState(0);

  // "+ Add Category" on the category strip (same popover as Tasks' "+ Add Group")
  const CATEGORY_SWATCHES = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#0ea5e9', '#14b8a6', '#f97316', '#6366f1'];
  const [newCatOpen, setNewCatOpen] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatColor, setNewCatColor] = useState('#3b82f6');
  const [creatingCat, setCreatingCat] = useState(false);
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!canCreate) return deny('create');
    const name = newCatName.trim();
    if (!name || creatingCat) return;
    setCreatingCat(true);
    try {
      const res = await fetch(`${BASE}/api/service-categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name, color: newCatColor }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        showToast('Category created', 'success');
        setNewCatOpen(false); setNewCatName('');
        fetchCategories();
        setFilterCategory(name);
      } else showToast(d.message || 'Error creating category', 'error');
    } catch (err) { showToast('Server error', 'error'); }
    finally { setCreatingCat(false); }
  };

  // ── Auto-assign Service IDs: never leave a service without one ──
  const [assigningIds, setAssigningIds] = useState(false);
  const missingIdCount = services.filter(s => !s.service_code || !String(s.service_code).trim()).length;
  const selectedMissingIds = services.filter(s => selectedIds.includes(s.id) && (!s.service_code || !String(s.service_code).trim())).map(s => s.id);
  const handleAutoAssignIds = (onlyIds) => {
    if (!canEdit) return deny('edit');
    const count = onlyIds ? onlyIds.length : missingIdCount;
    if (!count) return;
    showConfirm('Auto-assign Service IDs', `Assign the next free Service ID (SVC001, SVC002, …) to ${count} service(s) that don't have one? Existing IDs are never changed.`, async () => {
      setAssigningIds(true);
      try {
        const res = await fetch(`${BASE}/api/services/auto-assign-ids`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(onlyIds ? { ids: onlyIds } : {}),
        });
        const d = await res.json().catch(() => ({}));
        if (res.ok) { showToast(d.message || 'Service IDs assigned', 'success'); fetchServices(); }
        else showToast(d.message || 'Error assigning Service IDs', 'error');
      } catch (e) { showToast('Error assigning Service IDs', 'error'); }
      finally { setAssigningIds(false); }
    }, 'Assign IDs', '#2563eb');
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    if (!canDelete) return deny('delete');
    showConfirm('Delete Selected Services', `Delete ${selectedIds.length} selected service(s)? Any service referenced by an existing task or invoice will be skipped.`, async () => {
      try {
        const res = await fetch(`${BASE}/api/services/bulk-delete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ ids: selectedIds })
        });
        const data = await res.json();
        const deletedCount = data.deleted?.length || 0;
        const blocked = data.blocked || [];
        if (blocked.length === 0) {
          showToast(`Deleted ${deletedCount} service(s)`, 'success');
        } else {
          showToast(`Deleted ${deletedCount}, skipped ${blocked.length} — referenced by tasks/invoices (${blocked.map(b => b.name).join(', ')})`, 'error', 6000);
        }
        setSelectedIds([]);
        fetchServices();
      } catch (e) { showToast('Error deleting services', 'error'); }
    }, 'Delete Selected', '#ef4444');
  };

  const CAT_META = {
    'GST & Indirect Tax':       { color:'#16a34a', bg: darkMode?'rgba(16,185,129,0.12)':'#f0fdf4',  border: darkMode?'rgba(16,185,129,0.25)':'#bbf7d0'  },
    'Income Tax & TDS':         { color:'#2563eb', bg: darkMode?'rgba(59,130,246,0.12)' :'#eff6ff',  border: darkMode?'rgba(59,130,246,0.25)' :'#bfdbfe'  },
    'Company Law (ROC/MCA)':    { color:'#7c3aed', bg: darkMode?'rgba(139,92,246,0.12)' :'#f5f3ff',  border: darkMode?'rgba(139,92,246,0.25)' :'#ddd6fe'  },
    'Audit & Assurance':        { color:'#dc2626', bg: darkMode?'rgba(239,68,68,0.12)'  :'#fef2f2',  border: darkMode?'rgba(239,68,68,0.25)'  :'#fecaca'  },
    'Accounting & Bookkeeping': { color:'#d97706', bg: darkMode?'rgba(245,158,11,0.12)' :'#fffbeb',  border: darkMode?'rgba(245,158,11,0.25)' :'#fde68a'  },
    'Registration & Licenses':  { color:'#ea580c', bg: darkMode?'rgba(249,115,22,0.12)' :'#fff7ed',  border: darkMode?'rgba(249,115,22,0.25)' :'#fed7aa'  },
    'Payroll & HR':             { color:'#db2777', bg: darkMode?'rgba(236,72,153,0.12)' :'#fdf2f8',  border: darkMode?'rgba(236,72,153,0.25)' :'#fbcfe8'  },
    'Certification':            { color:'#4f46e5', bg: darkMode?'rgba(99,102,241,0.12)' :'#eef2ff',  border: darkMode?'rgba(99,102,241,0.25)' :'#c7d2fe'  },
  };
  catList.forEach(c => {
    if (c.color && !CAT_META[c.name]) CAT_META[c.name] = { color: c.color, bg: `${c.color}1A`, border: `${c.color}4D` };
  });

  const renderCell = (service, key) => {
    if (key === 'service_code') return service.service_code ? (
      <button onClick={() => setSelectedService(service)}
        style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, fontWeight:600, color: darkMode?'#93c5fd':'#2563eb' }}
        onMouseEnter={e => e.currentTarget.style.textDecoration='underline'}
        onMouseLeave={e => e.currentTarget.style.textDecoration='none'}>
        {service.service_code}
      </button>
    ) : <span style={{ color:T.text3 }}>—</span>;

    if (key === 'name') return (
      <button onClick={() => setSelectedService(service)}
        style={{ background:'none', border:'none', cursor:'pointer', textAlign:'left', padding:0 }}
        onMouseEnter={e => e.currentTarget.style.color='#f97316'}
        onMouseLeave={e => e.currentTarget.style.color= darkMode?'#f1f5f9':'#0f1f45'}>
        <p style={{ fontSize:13, fontWeight:600, color: darkMode?'#f1f5f9':'#0f1f45', margin:0 }}>{service.name}</p>
      </button>
    );

    if (key === 'category') {
      const m = CAT_META[service.category] || { color: darkMode?'#94a3b8':'#64748b', bg: darkMode?'rgba(100,116,139,0.12)':'#f8fafc', border: darkMode?'rgba(100,116,139,0.25)':'#e2e8f0' };
      return <span style={{ display:'inline-flex', alignItems:'center', padding:'3px 10px', borderRadius:8, fontSize:13, fontWeight:600, color:m.color, background:m.bg, border:`1px solid ${m.border}` }}>{service.category || 'Other'}</span>;
    }

    if (key === 'service_type') {
      const r = service.service_type === 'recurring';
      return <span style={{ display:'inline-flex', alignItems:'center', padding:'3px 10px', borderRadius:8, fontSize:13, fontWeight:600, color: r?(darkMode?'#60a5fa':'#2563eb'):(darkMode?'#94a3b8':'#64748b'), background: r?(darkMode?'rgba(59,130,246,0.12)':'#eff6ff'):(darkMode?'rgba(100,116,139,0.12)':'#f8fafc'), border:`1px solid ${r?(darkMode?'rgba(59,130,246,0.25)':'#bfdbfe'):(darkMode?'rgba(100,116,139,0.25)':'#e2e8f0')}` }}>{r?'Recurring':'One Time'}</span>;
    }

    if (key === 'gst_percent') return <span style={{ fontSize:13, fontWeight:600, color: darkMode?'#fbbf24':'#d97706' }}>{service.gst_percent == null || service.gst_percent === '' ? 18 : Number(service.gst_percent)}%</span>;
    if (key === 'default_amount') return <span style={{ fontSize:13, fontWeight:600, color: darkMode?'#34d399':'#16a34a' }}>{service.default_amount ? `₹${Number(service.default_amount).toLocaleString('en-IN')}` : '-'}</span>;
    if (key === 'billing_frequency') return <span style={{ fontSize:13, color: darkMode?'#cbd5e1':'#475569', textTransform:'capitalize' }}>{service.billing_frequency || '-'}</span>;

    if (key === 'status') return (
      <span style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 10px', borderRadius:6, background: service.status==='active'?(darkMode?'rgba(16,185,129,0.1)':'rgba(16,185,129,0.08)'):(darkMode?'rgba(239,68,68,0.1)':'rgba(239,68,68,0.08)'), border:`1px solid ${service.status==='active'?(darkMode?'rgba(16,185,129,0.3)':'rgba(16,185,129,0.25)'):(darkMode?'rgba(239,68,68,0.3)':'rgba(239,68,68,0.25)')}` }}>
        <span style={{ position:'relative', width:7, height:7, flexShrink:0 }}>
          <span style={{ position:'absolute', inset:0, borderRadius:'50%', background: service.status==='active'?'#10b981':'#ef4444', animation: service.status==='active'?'cx-pulse 2s ease-in-out infinite':'none' }}/>
          <span style={{ position:'absolute', inset:0, borderRadius:'50%', background: service.status==='active'?'#10b981':'#ef4444' }}/>
        </span>
        <span style={{ fontSize:12, fontWeight:600, color: service.status==='active'?(darkMode?'#34d399':'#059669'):(darkMode?'#f87171':'#dc2626') }}>
          {service.status === 'active' ? 'Active' : 'Inactive'}
        </span>
      </span>
    );

    return null;
  };

  // Search / type / status first; category last, so each category chip can show how many of
  // the currently matching services it holds (same idea as the Task Groups strip in Tasks).
  const beforeCategory = services.filter(s => {
    const q = searchTerm.toLowerCase();
    const matchSearch = !searchTerm ||
      s.name?.toLowerCase().includes(q) ||
      s.service_code?.toLowerCase().includes(q);
    const matchType = !filterType || s.service_type === filterType;
    const matchStatus = !filterStatus || s.status === filterStatus;
    return matchSearch && matchType && matchStatus;
  });
  const categoryOf = (svc) => svc.category || 'Other';
  const filtered = filterCategory === 'All Categories'
    ? beforeCategory
    : beforeCategory.filter(svc => categoryOf(svc) === filterCategory);
  const categoryCounts = beforeCategory.reduce((acc, svc) => { const c = categoryOf(svc); acc[c] = (acc[c] || 0) + 1; return acc; }, {});
  const categoryChips = Array.from(new Set([...categoryNames, ...services.map(categoryOf)]))
    .map(name => ({ name, count: categoryCounts[name] || 0, color: CAT_META[name]?.color || '#94a3b8' }))
    // Only categories that hold matching services — plus the one being viewed, so a just-created
    // (still empty) category doesn't vanish the moment it's selected.
    .filter(c => c.count > 0 || c.name === filterCategory);
  const filtersActive = !!searchTerm || filterCategory !== 'All Categories' || !!filterType || !!filterStatus;
  const clearFilters = () => { setSearchTerm(''); setFilterCategory('All Categories'); setFilterType(''); setFilterStatus(''); };

  const sorted = applySort(filtered, SERVICE_COLUMNS, sortKey, sortDir);
  const allSelected = sorted.length > 0 && sorted.every(s => selectedIds.includes(s.id));
  const someSelected = selectedIds.length > 0 && !allSelected;
  const toggleSelectAll = () => setSelectedIds(allSelected ? [] : sorted.map(s => s.id));

  const categoryColor = (cat) => {
    const colors = {
      'GST & Indirect Tax': 'bg-green-100 text-green-700',
      'Income Tax & TDS': 'bg-blue-100 text-blue-700',
      'Company Law (ROC/MCA)': 'bg-purple-100 text-purple-700',
      'Audit & Assurance': 'bg-red-100 text-red-700',
      'Accounting & Bookkeeping': 'bg-yellow-100 text-yellow-700',
      'Registration & Licenses': 'bg-orange-100 text-orange-700',
      'Payroll & HR': 'bg-pink-100 text-pink-700',
      'Certification': 'bg-indigo-100 text-indigo-700',
    };
    return colors[cat] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div style={{
      height:'100vh', padding:24, display:'flex', flexDirection:'column', overflow:'hidden', boxSizing:'border-box',
      background: darkMode
        ? 'radial-gradient(ellipse at 80% 0%,rgba(249,115,22,0.07) 0%,transparent 60%),radial-gradient(ellipse at 20% 100%,rgba(15,31,69,0.4) 0%,transparent 60%),#0a0f1e'
        : 'radial-gradient(ellipse at 80% 0%,rgba(249,115,22,0.05) 0%,transparent 50%),radial-gradient(ellipse at 0% 100%,rgba(15,31,69,0.04) 0%,transparent 50%),#f4f7ff',
    }}>
      <ConfirmModal open={!!confirm} title={confirm?.title||''} message={confirm?.message||''} onConfirm={()=>{confirm?.onConfirm();setConfirm(null);}} onCancel={()=>setConfirm(null)} confirmLabel={confirm?.confirmLabel||'Confirm'} confirmColor={confirm?.confirmColor||'#ef4444'} />

      {/* Tab bar — Services / Retainers / Templates with counts; the active tab's actions on the right (same pattern as Tasks) */}
      {(() => {
        const ghostBtn = { display:'flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:8, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:12.5, fontWeight:500, cursor:'pointer', flexShrink:0, whiteSpace:'nowrap' };
        const hoverOn = e => { e.currentTarget.style.borderColor='#3b82f6'; e.currentTarget.style.color='#3b82f6'; };
        const hoverOff = e => { e.currentTarget.style.borderColor=T.border2; e.currentTarget.style.color=T.text2; };
        const primaryBtn = (label, onClick) => (
          <button onClick={onClick}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:12.5, fontWeight:600, cursor:'pointer', flexShrink:0, whiteSpace:'nowrap' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            {label}
          </button>
        );
        return (
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10, padding:'6px 8px 6px 6px', background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:12}}>
        <div style={{ flex:1, minWidth:0 }}>
          <PillTabs
            tabs={[
              { key:'services',  label:'Services',  count: loading ? null : services.length },
              { key:'retainers', label:'Retainers', count: tabCounts.retainers },
              { key:'templates', label:'Templates', count: tabCounts.templates },
            ]}
            activeKey={activeSubTab} onChange={setActiveSubTab} T={T} darkMode={darkMode} />
        </div>

        {activeSubTab === 'services' && (
          <>
            {canEdit && missingIdCount > 0 && (
              <button onClick={() => handleAutoAssignIds(null)} disabled={assigningIds} title="Give every service without an ID the next free Service ID (SVC001, SVC002, …)"
                style={{ ...ghostBtn, border:'1px solid #fde68a', background:darkMode?'rgba(245,158,11,0.12)':'#fffbeb', color:'#b45309', fontWeight:600, opacity: assigningIds?0.6:1 }}>
                {assigningIds ? 'Assigning…' : `Auto-assign IDs (${missingIdCount})`}
              </button>
            )}
            {canExport && (
              <button onClick={handleExport} title="Export services to Excel" style={ghostBtn} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Export
              </button>
            )}
            {canCreate && (
              <button onClick={() => setShowImportModal(true)} title="Import services from Excel" style={ghostBtn} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                Import
              </button>
            )}
            {canCreate && primaryBtn('New Service', () => setShowAddModal(true))}
          </>
        )}
        {activeSubTab === 'retainers' && canCreate && primaryBtn('New Retainer', () => setNewRetainerTick(n => n + 1))}
        {activeSubTab === 'templates' && canCreate && primaryBtn('New Template', () => setNewTemplateTick(n => n + 1))}
      </div>
        );
      })()}

      {activeSubTab === 'services' && (
        <>
          {/* Search + filter bar */}
          <div style={{ background:T.bg2, borderRadius:12, border:`1px solid ${T.cardBorder}`, padding:10, marginBottom:10 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <input type="text" placeholder="Search by service name or service ID..."
                value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                style={{ flex:1, minWidth:0, border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'9px 14px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }} />
              <div style={{ width:160, flexShrink:0 }}>
                <SvcSelect value={filterType || 'all'} onChange={v => setFilterType(v === 'all' ? '' : v)} T={T} darkMode={darkMode}
                  options={[{ value:'all', label:'All Types' }, { value:'one_time', label:'One Time' }, { value:'recurring', label:'Recurring' }]} />
              </div>
              <div style={{ width:160, flexShrink:0 }}>
                <SvcSelect value={filterStatus || 'all'} onChange={v => setFilterStatus(v === 'all' ? '' : v)} T={T} darkMode={darkMode}
                  options={[{ value:'all', label:'All Status' }, { value:'active', label:'Active' }, { value:'inactive', label:'Inactive' }]} />
              </div>
              {filtersActive && (
                <button onClick={clearFilters}
                  style={{ padding:'8px 12px', borderRadius:8, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text3, fontSize:12, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>
                  Clear Filters
                </button>
              )}
              <p style={{ fontSize:12, color:T.text3, whiteSpace:'nowrap', margin:0 }}>{filtered.length} found</p>
            </div>
          </div>

          {/* Bulk action bar — appears once one or more services are ticked (same as Tasks) */}
          {selectedIds.length > 0 && (
            <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:12, padding:'10px 14px', borderRadius:12,
              background: darkMode?'rgba(59,130,246,0.1)':'#eff6ff', border:`1px solid ${darkMode?'rgba(59,130,246,0.25)':'#bfdbfe'}` }}>
              <span style={{ fontSize:13, fontWeight:700, color:'#1d4ed8' }}>{selectedIds.length} selected</span>
              {canEdit && selectedMissingIds.length > 0 && (
                <button type="button" disabled={assigningIds} onClick={() => handleAutoAssignIds(selectedMissingIds)} title="Give the selected services without an ID the next free Service ID"
                  style={{ padding:'7px 14px', borderRadius:8, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text, fontSize:12.5, fontWeight:600, cursor:'pointer', opacity: assigningIds?0.6:1 }}>
                  Assign Service ID ({selectedMissingIds.length})
                </button>
              )}
              {canDelete && (
                <button type="button" onClick={handleBulkDelete}
                  style={{ padding:'7px 14px', borderRadius:8, border:'1px solid #fecaca', background: darkMode?'rgba(239,68,68,0.12)':'#fef2f2', color:'#dc2626', fontSize:12.5, fontWeight:600, cursor:'pointer' }}>
                  Delete
                </button>
              )}
              <button type="button" onClick={() => setSelectedIds([])}
                style={{ padding:'7px 10px', borderRadius:8, border:'none', background:'none', color:T.text3, fontSize:12, cursor:'pointer' }}>
                Clear selection
              </button>
            </div>
          )}
        </>
      )}

      {/* Route to sub-tabs */}
      {activeSubTab === 'retainers' && <RetainersTab token={token} darkMode={darkMode} T={T} showConfirm={showConfirm}
        newTick={newRetainerTick} onCount={n => setTabCounts(c => ({ ...c, retainers: n }))} />}
      {activeSubTab === 'templates' && <TemplatesTab token={token} darkMode={darkMode} T={T} showConfirm={showConfirm}
        newTick={newTemplateTick} onCount={n => setTabCounts(c => ({ ...c, templates: n }))} />}
      {activeSubTab === 'services' && <div style={{display:'contents'}}>

      {/* Services Table */}
      <div style={{ flex:1, minHeight:0, background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, overflow:'hidden', display:'flex', flexDirection:'column', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }} onClick={() => setOpenKebabId(null)}>
        {/* Service categories — pills on top of the table; empty categories stay hidden */}
        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderBottom:`1px solid ${T.border}` }}>
          <ChipOverflowStrip
            items={[{ key:'All Categories', name:'All Categories', count: beforeCategory.length },
              ...categoryChips.map(c => ({ key:c.name, name:c.name, count:c.count, color:c.color }))]}
            activeKey={filterCategory} onSelect={setFilterCategory} T={T} darkMode={darkMode} orderKey="services-categories" />
          {/* Pinned outside the chip strip so it is always visible */}
          {canCreate && <div style={{ position:'relative', flexShrink:0 }} onClick={e => e.stopPropagation()}>
            <button type="button" onClick={() => { setNewCatOpen(o => !o); setNewCatName(''); }}
              style={{ display:'flex', alignItems:'center', gap:5, padding:'4px 10px', borderRadius:8, whiteSpace:'nowrap', cursor:'pointer',
                fontSize:12, fontWeight:600, border:'1px dashed #3b82f6', background: newCatOpen ? (darkMode ? 'rgba(59,130,246,0.15)' : '#eff6ff') : 'transparent', color:'#3b82f6' }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add Category
            </button>
            {newCatOpen && (
              <form onSubmit={handleCreateCategory}
                style={{ position:'absolute', top:'calc(100% + 6px)', right:0, zIndex:95, width:260, padding:14, background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.15)' }}>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:6 }}>Category name</label>
                <input autoFocus type="text" maxLength={80} placeholder="e.g. Trust & NGO" value={newCatName}
                  onChange={e => setNewCatName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Escape') setNewCatOpen(false); }}
                  style={{ width:'100%', boxSizing:'border-box', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'8px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }} />
                <div style={{ display:'flex', gap:6, margin:'10px 0 12px', flexWrap:'wrap' }}>
                  {CATEGORY_SWATCHES.map(c => (
                    <button key={c} type="button" onClick={() => setNewCatColor(c)}
                      style={{ width:18, height:18, borderRadius:'50%', background:c, cursor:'pointer', border: newCatColor === c ? `2px solid ${T.text}` : '2px solid transparent' }} />
                  ))}
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button type="submit" disabled={creatingCat || !newCatName.trim()}
                    style={{ flex:1, padding:'8px 12px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:12.5, fontWeight:600, cursor:'pointer', opacity: creatingCat || !newCatName.trim() ? 0.6 : 1 }}>
                    {creatingCat ? 'Creating...' : 'Create'}
                  </button>
                  <button type="button" onClick={() => setNewCatOpen(false)}
                    style={{ padding:'8px 12px', borderRadius:8, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:12.5, cursor:'pointer' }}>
                    Cancel
                  </button>
                </div>
                <p style={{ fontSize:11, color:T.text3, margin:'10px 0 0' }}>Manage all categories in Settings → Service Categories.</p>
              </form>
            )}
          </div>}
        </div>
        <div style={{ flex:1, overflowX:'auto', overflowY:'auto', scrollbarWidth:'none', msOverflowStyle:'none' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', position:'sticky', top:0, zIndex:10 }}>
            <tr>
              {selCol && (
                <th style={{ padding:'10px 12px', width:36 }} onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={allSelected} ref={el => { if (el) el.indeterminate = someSelected; }} onChange={toggleSelectAll} title="Select all (current filter)" style={{ cursor:'pointer' }} />
                </th>
              )}
              {orderedVisibleKeys.map(key => {
                const col = SERVICE_COLUMNS.find(c => c.key === key);
                return (
                  <SortableTh key={key} label={col?.label} sortKey={key} activeKey={sortKey} dir={sortDir} onSort={onSort} resizeId="services" />
                );
              })}
              <th style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>Actions</th>
              <th style={{ padding:'10px 8px', textAlign:'right', width:56, background:'transparent' }}>
                {cc.menu}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={visibleColumns.length+2+(selCol?1:0)} style={{ padding:'32px 16px', textAlign:'center', color:T.text3 }}>Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={visibleColumns.length+2+(selCol?1:0)} style={{ padding:'32px 16px', textAlign:'center', color:T.text3 }}>No services found.</td></tr>
            ) : (
              sorted.map((service, idx) => (
                <tr key={service.id}
                  style={{ borderBottom:`1px solid ${T.border}`, background: darkMode?(idx%2===0?T.bg2:T.bg3):(idx%2===0?'#fff':'#fafbff'), transition:'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = darkMode?'#1e3a5f':'#f0f4ff'}
                  onMouseLeave={e => e.currentTarget.style.background = darkMode?(idx%2===0?T.bg2:T.bg3):(idx%2===0?'#fff':'#fafbff')}>
                  {selCol && (
                    <td style={{ padding:'10px 12px' }} onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedIds.includes(service.id)} onChange={() => {}}
                        onClick={e => toggleSelect(service.id, e.shiftKey)} title="Shift+click to select a range" style={{ cursor:'pointer' }} />
                    </td>
                  )}
                  {orderedVisibleKeys.map(key => (
                    <td key={key} style={{
                      ...(savedColWidth(key) ? { width:savedColWidth(key), minWidth:savedColWidth(key), maxWidth:savedColWidth(key), boxSizing:'border-box' } : null),
                      padding:'10px 16px', fontSize:13, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', color:T.text2,
                    }}>
                      {renderCell(service, key)}
                    </td>
                  ))}
                  <td style={{ padding:'10px 16px' }}>
                    <div style={{ position:'relative' }} onClick={e => e.stopPropagation()}>
                      <button onClick={() => setOpenKebabId(openKebabId===service.id?null:service.id)}
                        style={{ width:32, height:32, borderRadius:8, border:'none', background:'none', color:T.text2, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                        onMouseLeave={e => e.currentTarget.style.background='none'}>
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                          <circle cx="10" cy="4" r="1.8"/><circle cx="10" cy="10" r="1.8"/><circle cx="10" cy="16" r="1.8"/>
                        </svg>
                      </button>
                      {openKebabId === service.id && (
                        <div style={{ position:'absolute', right:0, top:36, background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 8px 24px rgba(15,31,69,0.12)', zIndex:50, width:144, padding:6 }}>
                          <button onClick={() => { setOpenKebabId(null); setSelectedService(service); }}
                            style={{ width:'100%', textAlign:'left', padding:'8px 12px', borderRadius:8, border:'none', background:'none', color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}
                            onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                            onMouseLeave={e => e.currentTarget.style.background='none'}>
                            <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 010 3L12 12l-4 1 1-4 6.5-6.5a2.121 2.121 0 013 0z"/></svg>
                            {canEdit ? 'Edit' : 'View'}
                          </button>
                          {canDelete && (
                            <button onClick={() => handleDeleteService(service.id, service.name)}
                              style={{ width:'100%', textAlign:'left', padding:'8px 12px', borderRadius:8, border:'none', background:'none', color:'#ef4444', fontSize:13, fontWeight:500, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}
                              onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2'}
                              onMouseLeave={e => e.currentTarget.style.background='none'}>
                              <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 17 6"/><path d="M16 6l-1 13H5L4 6"/><path d="M8 6V4h4v2"/></svg>
                              Delete
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && canCreate && (
        <ImportServicesModal
          token={token}
          services={services}
          onClose={() => setShowImportModal(false)}
          onSuccess={() => { fetchServices(); setShowImportModal(false); }}
          darkMode={darkMode} T={T} />
      )}

      {/* Add Modal */}
      {showAddModal && canCreate && (
        <ServiceFormModal token={token} onClose={() => setShowAddModal(false)}
          onSuccess={() => { fetchServices(); setShowAddModal(false); }}
          darkMode={darkMode} T={T} existingServices={services} categories={categoryNames} onCategoryCreated={fetchCategories} />
      )}

      {/* Edit/Detail Modal */}
      {selectedService && (
        <ServiceDetailModal
          service={selectedService} token={token}
          onClose={() => setSelectedService(null)}
          onSuccess={() => { fetchServices(); setSelectedService(null); }}
          darkMode={darkMode} T={T} existingServices={services} categories={categoryNames} onCategoryCreated={fetchCategories} />
      )}
    </div>}
    </div>
  );
}

function Services(props) {
  return (
    <ToastProvider>
      <ServicesBody {...props} />
    </ToastProvider>
  );
}

// Word-overlap similarity check — flags likely-duplicate service names (e.g.
// "Change in Object Clause" vs "Alteration in Object Clause") without hard-
// blocking, since two overlapping-worded services can legitimately differ.
function findSimilarServices(name, existingServices, threshold = 0.4) {
  const words = (s) => new Set(s.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(w => w.length > 2));
  const target = words(name);
  if (target.size === 0) return [];
  return (existingServices || [])
    .map(s => {
      const other = words(s.name || '');
      if (other.size === 0) return null;
      const overlap = [...target].filter(w => other.has(w)).length;
      const score = overlap / Math.max(target.size, other.size);
      return { name: s.name, score };
    })
    .filter(x => x && x.score >= threshold && x.name.toLowerCase() !== name.toLowerCase())
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);
}

// Shared input style factory — used by every field in the shared settings components below,
// so create/edit stay pixel-identical without threading a style object through every caller.
function svcInputStyle(T) {
  return { width: '100%', border: `1px solid ${T.inputBorder}`, borderRadius: 10, padding: '9px 12px', fontSize: 13, outline: 'none', background: T.inputBg, color: T.text, boxSizing: 'border-box' };
}
function svcToggleStyle(checked, darkMode) {
  return { position: 'relative', width: 38, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer', background: checked ? '#3b82f6' : (darkMode ? '#334155' : '#d1d5db'), transition: 'background 0.2s', flexShrink: 0 };
}
function SvcToggle({ checked, onToggle, darkMode }) {
  return (
    <button type="button" onClick={onToggle} style={svcToggleStyle(checked, darkMode)}>
      <span style={{ position: 'absolute', top: 2, left: checked ? 20 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </button>
  );
}
function SvcLabel({ children }) {
  return <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{children}</label>;
}

function svcOptionStyle(sel, darkMode, T) {
  return { width: '100%', textAlign: 'left', padding: '8px 12px', borderRadius: 8, border: 'none', background: sel ? (darkMode ? 'rgba(59,130,246,0.18)' : '#eff6ff') : 'transparent', color: sel ? '#3b82f6' : T.text, fontSize: 13, fontWeight: sel ? 600 : 400, cursor: 'pointer', transition: 'background 0.12s', display: 'flex', alignItems: 'center', gap: 8 };
}

// One consistent custom dropdown look for Category / GST / assignee pickers — rounded panel,
// rounded hover pills (never square), hidden scrollbar, optional pinned footer. The panel is
// portalled to <body> and fixed-positioned from the trigger's rect so it never gets clipped
// by a modal's `overflow:auto` body. z-index sits above the service modals (which are z-50).
function SvcDropdownPanel({ anchorRef, onRequestClose, T, darkMode, children, footer }) {
  const [pos, setPos] = useState(null);
  const panelRef = useRef(null);
  useEffect(() => {
    const place = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const belowSpace = window.innerHeight - r.bottom;
      const openUp = belowSpace < 280 && r.top > 280;
      setPos({ left: r.left, width: r.width, top: openUp ? undefined : r.bottom + 6, bottom: openUp ? window.innerHeight - r.top + 6 : undefined });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    const h = (e) => {
      if (anchorRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      onRequestClose();
    };
    document.addEventListener('mousedown', h);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); document.removeEventListener('mousedown', h); };
  }, [anchorRef, onRequestClose]);
  if (!pos) return null;
  return ReactDOM.createPortal(
    <div ref={panelRef} style={{ position: 'fixed', left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, background: T.bg2, border: `1px solid ${T.border2}`, borderRadius: 12, boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 32px rgba(15,31,69,0.14)', zIndex: 100000, overflow: 'hidden' }}>
      <style>{`.svc-no-scroll::-webkit-scrollbar{display:none}`}</style>
      <div className="svc-no-scroll" style={{ maxHeight: 240, overflowY: 'auto', padding: 6, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {children}
      </div>
      {footer && <div style={{ borderTop: `1px solid ${T.border}` }}>{footer}</div>}
    </div>,
    document.body
  );
}

// Single-select styled dropdown — used for Category (with the "+ Add New Category" footer)
// and GST %. `options` = [{ value, label }].
function SvcSelect({ value, onChange, options, placeholder = 'Select…', footer, T, darkMode }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const selected = options.find(o => String(o.value) === String(value));
  return (
    <div style={{ position: 'relative' }}>
      <button ref={triggerRef} type="button" onClick={() => setOpen(o => !o)}
        style={{ ...svcInputStyle(T), textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
        <span style={{ color: selected ? T.text : T.text3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected ? selected.label : placeholder}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginLeft: 6, transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <SvcDropdownPanel anchorRef={triggerRef} onRequestClose={() => setOpen(false)} T={T} darkMode={darkMode}
          footer={footer && footer({ close: () => setOpen(false) })}>
          {options.map(o => {
            const sel = String(o.value) === String(value);
            return (
              <button key={o.value} type="button" onClick={() => { onChange(o.value); setOpen(false); }}
                style={svcOptionStyle(sel, darkMode, T)}
                onMouseEnter={e => { if (!sel) e.currentTarget.style.background = darkMode ? 'rgba(148,163,184,0.12)' : '#f1f5f9'; }}
                onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                {o.label}
              </button>
            );
          })}
        </SvcDropdownPanel>
      )}
    </div>
  );
}

// Multi-user picker — same panel look as SvcSelect, plus checkboxes, an "Assign to all"
// shortcut row, hidden scrollbar, and removable chips below. `value` is user-id strings.
function UserMultiPicker({ value, onChange, users, T, darkMode, placeholder = '— Select users —' }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const toggleUser = (uid) => {
    const s = String(uid);
    onChange(value.includes(s) ? value.filter(id => id !== s) : [...value, s]);
  };
  const allIds = users.map(u => String(u.id));
  const allSelected = allIds.length > 0 && allIds.every(id => value.includes(id));
  return (
    <div>
      <div style={{ position: 'relative' }}>
        <button ref={triggerRef} type="button" onClick={() => setOpen(o => !o)}
          style={{ ...svcInputStyle(T), textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
          <span style={{ color: value.length ? T.text : T.text3 }}>{value.length ? `${value.length} user(s) selected` : placeholder}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginLeft: 6, transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}><polyline points="6 9 12 15 18 9" /></svg>
        </button>
        {open && (
          <SvcDropdownPanel anchorRef={triggerRef} onRequestClose={() => setOpen(false)} T={T} darkMode={darkMode}>
            <button type="button" onClick={() => onChange(allSelected ? [] : allIds)}
              style={{ ...svcOptionStyle(false, darkMode, T), fontWeight: 600, color: '#3b82f6' }}
              onMouseEnter={e => e.currentTarget.style.background = darkMode ? 'rgba(148,163,184,0.12)' : '#f1f5f9'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              {allSelected ? 'Clear all' : 'Assign to all'}
            </button>
            {users.map(u => {
              const sel = value.includes(String(u.id));
              return (
                <button key={u.id} type="button" onClick={() => toggleUser(u.id)}
                  style={svcOptionStyle(sel, darkMode, T)}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = darkMode ? 'rgba(148,163,184,0.12)' : '#f1f5f9'; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                  <input type="checkbox" readOnly checked={sel} style={{ pointerEvents: 'none' }} />
                  {u.full_name}
                </button>
              );
            })}
          </SvcDropdownPanel>
        )}
      </div>
      {value.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {value.map(uid => {
            const u = users.find(x => String(x.id) === uid);
            return (
              <span key={uid} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 14, background: darkMode ? 'rgba(59,130,246,0.15)' : '#dbeafe', color: '#3b82f6', fontSize: 12, fontWeight: 600 }}>
                {u?.full_name || uid}
                <button type="button" onClick={() => toggleUser(uid)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', fontSize: 13, lineHeight: 1, padding: 0 }}>×</button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Category picker: SvcSelect + an inline "+ Add New Category" quick-create in the footer —
// posts straight to /api/service-categories, no detour through Settings.
// `allOption` prepends an "All Categories" entry (list filter); `selectOnCreate={false}` keeps the
// current value when a category is created from the footer (a filter shouldn't jump to the new,
// still-empty category).
function CategorySelect({ value, onChange, categories, token, onCategoryCreated, T, darkMode, allOption = false, selectOnCreate = true, popoverForm = false }) {
  const { showToast } = useToast();
  const { canCreate, deny } = useServiceAccess(showToast);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3b82f6');
  const [saving, setSaving] = useState(false);
  const COLORS = ['#6366f1', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#0ea5e9', '#14b8a6', '#f97316'];

  const handleSave = async () => {
    if (!name.trim()) return;
    if (!canCreate) return deny('create');
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/service-categories`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name: name.trim(), color }),
      });
      if (res.ok) {
        onCategoryCreated?.(name.trim());
        if (selectOnCreate) onChange(name.trim());
        setAdding(false); setName('');
        showToast('Category created', 'success');
      } else showToast('Error creating category', 'error');
    } catch (e) { showToast('Server error', 'error'); }
    finally { setSaving(false); }
  };

  if (adding && !popoverForm) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 10, border: `1px dashed ${T.border2}`, borderRadius: 10 }}>
        <input autoFocus type="text" placeholder="New category name" value={name} onChange={e => setName(e.target.value)} style={svcInputStyle(T)} />
        <div style={{ display: 'flex', gap: 6 }}>
          {COLORS.map(c => (
            <button key={c} type="button" onClick={() => setColor(c)}
              style={{ width: 20, height: 20, borderRadius: '50%', background: c, cursor: 'pointer', border: color === c ? `2px solid ${T.text}` : '2px solid transparent' }} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={handleSave} disabled={saving} style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button type="button" onClick={() => { setAdding(false); setName(''); }} style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: `1px solid ${T.border2}`, background: 'none', color: T.text2, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  const popover = adding && popoverForm && (
    <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 100001, width: 270, display: 'flex', flexDirection: 'column', gap: 8, padding: 12, background: T.bg2, border: `1px solid ${T.border2}`, borderRadius: 12, boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.15)' }}>
      <input autoFocus type="text" placeholder="New category name" value={name} onChange={e => setName(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') { setAdding(false); setName(''); } }} style={svcInputStyle(T)} />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {COLORS.map(c => (
          <button key={c} type="button" onClick={() => setColor(c)}
            style={{ width: 20, height: 20, borderRadius: '50%', background: c, cursor: 'pointer', border: color === c ? `2px solid ${T.text}` : '2px solid transparent' }} />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={handleSave} disabled={saving} style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button type="button" onClick={() => { setAdding(false); setName(''); }} style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: `1px solid ${T.border2}`, background: 'none', color: T.text2, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ position: 'relative' }}>
    <SvcSelect value={value} onChange={onChange} T={T} darkMode={darkMode} placeholder="Select category…"
      options={[...(allOption ? [{ value: 'All Categories', label: 'All Categories' }] : []), ...categories.map(c => ({ value: c, label: c }))]}
      footer={!canCreate ? undefined : ({ close }) => (
        <button type="button" onClick={() => { close(); setAdding(true); }}
          style={{ width: '100%', textAlign: 'left', padding: '9px 14px', border: 'none', background: 'transparent', color: '#3b82f6', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          onMouseEnter={e => e.currentTarget.style.background = darkMode ? 'rgba(148,163,184,0.12)' : '#f1f5f9'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          + Add New Category
        </button>
      )} />
    {popover}
    </div>
  );
}

// The frequency-dependent Creation Date / Due Date pickers — shape changes per frequency to
// match the reference product exactly: Monthly is day-only, Quarterly/Half-Yearly add a
// month-of-cycle dropdown (labelled differently but both are just an FY-month-offset), and
// Yearly swaps to a plain date picker. All four write the exact same four form fields
// (auto_task_creation_day/_month_offset, auto_task_due_day/_month_offset) so the backend
// formula never needs to know which UI produced them.
function RecurringScheduleFields({ form, setForm, T, darkMode }) {
  const freq = form.auto_task_frequency;
  const dayOptions = Array.from({ length: 31 }, (_, i) => i + 1);
  const monthOptions = freq === 'quarterly' ? QUARTER_MONTH_OPTIONS : freq === 'half_yearly' ? HALF_YEAR_MONTH_OPTIONS : freq === 'annually' ? YEARLY_MONTH_OPTIONS : null;

  const dateRow = (label, dayField, monthField) => {
    return (
      <div>
        <SvcLabel>{label}</SvcLabel>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ width: 92, flexShrink: 0 }}>
            <SvcSelect value={String(form[dayField])} T={T} darkMode={darkMode}
              onChange={v => setForm(f => ({ ...f, [dayField]: Number(v) }))}
              options={dayOptions.map(d => ({ value: String(d), label: ordinal(d) }))} />
          </div>
          {monthOptions && (
            <>
              <span style={{ fontSize: 12, color: T.text3, flexShrink: 0 }}>of</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <SvcSelect value={String(form[monthField])} T={T} darkMode={darkMode}
                  onChange={v => setForm(f => ({ ...f, [monthField]: Number(v) }))}
                  options={monthOptions.map(o => ({ value: String(o.v), label: o.l }))} />
              </div>
            </>
          )}
          <span style={{ fontSize: 12, color: T.text3, flexShrink: 0, whiteSpace: 'nowrap' }}>
            {freq === 'monthly' ? 'of every month' : freq === 'quarterly' ? 'month of every quarter' : freq === 'half_yearly' ? 'month' : freq === 'annually' ? 'of every year' : ''}
          </span>
        </div>
      </div>
    );
  };

  return (
    <>
      <div>
        <SvcLabel>Auto Task Creation Frequency *</SvcLabel>
        <SvcSelect value={freq} T={T} darkMode={darkMode}
          onChange={v => setForm(f => ({ ...f, auto_task_frequency: v }))}
          placeholder="— Select Frequency —"
          options={[
            { value: 'monthly', label: 'Monthly' }, { value: 'quarterly', label: 'Quarterly' },
            { value: 'half_yearly', label: 'Half Yearly' }, { value: 'annually', label: 'Yearly' },
          ]} />
      </div>

      {freq && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 14 }}>
            {dateRow('Creation Date *', 'auto_task_creation_day', 'auto_task_creation_month_offset')}
            {dateRow('Due Date *', 'auto_task_due_day', 'auto_task_due_month_offset')}
          </div>

          <div style={{ marginTop: 14 }}>
            <SvcLabel>Target Date (Creation + N Days)</SvcLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, color: T.text2, flexShrink: 0 }}>Creation Date +</span>
              <input type="number" min="0" max="90" value={form.auto_task_target_offset_days}
                onChange={e => setForm(f => ({ ...f, auto_task_target_offset_days: Number(e.target.value) }))} style={{ ...svcInputStyle(T), width: 80 }} />
              <span style={{ fontSize: 13, color: T.text2, flexShrink: 0 }}>Days</span>
            </div>
          </div>

          <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 10, background: darkMode ? 'rgba(59,130,246,0.1)' : '#eff6ff', border: `1px solid ${darkMode ? 'rgba(59,130,246,0.2)' : '#bfdbfe'}` }}>
            <p style={{ fontSize: 12, color: darkMode ? '#93c5fd' : '#1d4ed8', margin: 0, lineHeight: 1.5 }}>
              Kindly note that all tasks will be scheduled for future dates exclusively. This means that tasks can only be created for tomorrow or subsequent days. Tasks for today's date or past days cannot be generated.
            </p>
          </div>

          <div style={{ marginTop: 14 }}>
            <SvcLabel>Which period should each task cover?</SvcLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { v: 'recently_completed', l: 'Create tasks for the recently completed period' },
                { v: 'currently_running', l: 'Create tasks for the currently running period' },
              ].map(opt => (
                <label key={opt.v} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.text2, cursor: 'pointer' }}>
                  <input type="radio" name={`auto_task_period_mode_${form._formId || ''}`} value={opt.v} checked={form.auto_task_period_mode === opt.v}
                    onChange={() => setForm(f => ({ ...f, auto_task_period_mode: opt.v }))} />
                  {opt.l}
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}

// BASIC DETAILS — shared by both "Add New Service" and the Service Detail Settings tab, so
// the two can never drift out of sync again.
function ServiceBasicDetailsFields({ form, setForm, users, categories, onCategoryCreated, token, T, darkMode, showServiceCode = true, nameHint }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <SvcLabel>Service Name *</SvcLabel>
        <input required type="text" placeholder="e.g. GST Filing Monthly" value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={svcInputStyle(T)} />
        {nameHint}
      </div>

      {showServiceCode && (
        <div>
          <SvcLabel>Service ID</SvcLabel>
          <input type="text" placeholder="e.g. TAX001" value={form.service_code}
            onChange={e => setForm(f => ({ ...f, service_code: e.target.value }))} style={svcInputStyle(T)} />
        </div>
      )}

      <div>
        <SvcLabel>Category</SvcLabel>
        <CategorySelect value={form.category} onChange={v => setForm(f => ({ ...f, category: v }))}
          categories={categories} token={token} onCategoryCreated={onCategoryCreated} T={T} darkMode={darkMode} />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 24px', paddingTop: 8, borderTop: `1px solid ${T.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SvcToggle checked={form.status === 'active'} darkMode={darkMode}
            onToggle={() => setForm(f => ({ ...f, status: f.status === 'active' ? 'inactive' : 'active' }))} />
          <span style={{ fontSize: 13, color: T.text2 }}>Is Enabled?</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SvcToggle checked={form.is_checklist_required} darkMode={darkMode}
            onToggle={() => setForm(f => ({ ...f, is_checklist_required: !f.is_checklist_required }))} />
          <span style={{ fontSize: 13, color: T.text2 }}>Is Checklist Completion Required?</span>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <SvcToggle checked={form.service_type === 'recurring'} darkMode={darkMode}
          onToggle={() => setForm(f => ({ ...f, service_type: f.service_type === 'recurring' ? 'one_time' : 'recurring' }))} />
        <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Is Recurring?</span>
      </div>

      {form.service_type === 'recurring' && (
        <div style={{ borderRadius: 12, padding: 16, background: darkMode ? 'rgba(59,130,246,0.08)' : '#eff6ff', border: `1px solid ${darkMode ? 'rgba(59,130,246,0.2)' : '#bfdbfe'}` }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: '#3b82f6', margin: '0 0 12px' }}>⚙ Auto Task Generation (Recurring)</p>
          <RecurringScheduleFields form={form} setForm={setForm} T={T} darkMode={darkMode} />

          {form.auto_task_frequency && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
                <span style={{ fontSize: 13, color: T.text2 }}>Assign Auto Tasks to Users of Respective Clients</span>
                <SvcToggle checked={form.auto_assign_to_clients} darkMode={darkMode}
                  onToggle={() => setForm(f => ({ ...f, auto_assign_to_clients: !f.auto_assign_to_clients }))} />
              </div>
              {!form.auto_assign_to_clients && (
                <div style={{ marginTop: 10 }}>
                  <SvcLabel>Assign Auto Tasks to (Users)</SvcLabel>
                  <UserMultiPicker value={form.auto_assign_user_ids} users={users} T={T} darkMode={darkMode}
                    onChange={ids => setForm(f => ({ ...f, auto_assign_user_ids: ids }))} />
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div>
        <SvcLabel>Default Users (Assign to)</SvcLabel>
        <UserMultiPicker value={form.default_assign_user_ids} users={users} T={T} darkMode={darkMode}
          onChange={ids => setForm(f => ({ ...f, default_assign_user_ids: ids }))} />
      </div>
    </div>
  );
}

// BILLING SETTINGS — shared the same way as Basic Details above.
function ServiceBillingSettingsFields({ form, setForm, T, darkMode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <SvcLabel>Default Billing Rate (Excl. of Tax)</SvcLabel>
        <input type="number" placeholder="0.00" value={form.default_amount}
          onChange={e => setForm(f => ({ ...f, default_amount: e.target.value }))} style={svcInputStyle(T)} />
      </div>
      <div>
        <SvcLabel>GST %</SvcLabel>
        <SvcSelect value={String(Number(form.gst_percent || 0))} T={T} darkMode={darkMode}
          onChange={v => setForm(f => ({ ...f, gst_percent: Number(v) }))}
          options={[0, 5, 12, 18, 28].map(g => ({ value: String(g), label: `${g}%` }))} />
      </div>
      <div>
        <SvcLabel>SAC Code</SvcLabel>
        <input type="text" placeholder="e.g. 998231" value={form.sac_code}
          onChange={e => setForm(f => ({ ...f, sac_code: e.target.value }))} style={svcInputStyle(T)} />
      </div>
      {form.default_amount && (() => {
        const rate = Number(form.default_amount) || 0;
        const gst = Number(form.gst_percent) || 0;
        return (
          <div style={{ background: darkMode ? 'rgba(59,130,246,0.1)' : '#eff6ff', border: `1px solid ${darkMode ? 'rgba(59,130,246,0.2)' : '#bfdbfe'}`, borderRadius: 10, padding: '12px 16px' }}>
            <p style={{ fontSize: 12, color: T.text2, margin: '0 0 4px' }}>Rate: ₹{rate.toLocaleString('en-IN')}</p>
            <p style={{ fontSize: 12, color: T.text2, margin: '0 0 4px' }}>GST ({gst}%): ₹{(rate * gst / 100).toLocaleString('en-IN')}</p>
            <p style={{ fontSize: 13, color: '#3b82f6', fontWeight: 700, margin: 0 }}>Total: ₹{(rate * (1 + gst / 100)).toLocaleString('en-IN')}</p>
          </div>
        );
      })()}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}` }}>
        <span style={{ fontSize: 13, color: T.text2 }}>Mark tasks billable by default</span>
        <SvcToggle checked={form.is_billable_by_default} darkMode={darkMode}
          onToggle={() => setForm(f => ({ ...f, is_billable_by_default: !f.is_billable_by_default }))} />
      </div>
      <div>
        <SvcLabel>
          Description
          <span style={{ color: '#3b82f6', marginLeft: 6, textTransform: 'none', fontWeight: 400 }}>(auto-fetched when creating invoice)</span>
        </SvcLabel>
        <textarea rows="5"
          placeholder="e.g. Professional fees for GST Return Filing including:&#10;1. Data compilation and verification&#10;2. GSTR-1 preparation and filing&#10;3. GSTR-3B preparation and filing&#10;4. Acknowledgement sharing"
          value={form.description || ''}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={{ ...svcInputStyle(T), resize: 'vertical' }} />
      </div>
    </div>
  );
}

// SERVICE FORM MODAL — Add New Service
function ServiceFormModal({ token, onClose, onSuccess, darkMode = false, T: theme, existingServices = [], categories = [], onCategoryCreated }) {
  const { showToast } = useToast();
  const { canCreate, deny } = useServiceAccess(showToast);
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('basic');
  const [form, setForm] = useState({
    name: '', service_code: '', category: categories[0] || 'Other', service_type: 'one_time',
    billing_frequency: '', default_amount: '', gst_percent: 18,
    sac_code: '', description: '', status: 'active',
    is_checklist_required: false, is_billable_by_default: true,
    auto_task_frequency: '', auto_assign_to: '', auto_task_advance_days: 7,
    auto_task_creation_day: 1, auto_task_due_day: 11, auto_task_target_offset_days: 9,
    auto_task_period_mode: 'recently_completed', auto_assign_user_ids: [], auto_assign_to_clients: false,
    auto_task_creation_month_offset: 0, auto_task_due_month_offset: 0, default_assign_user_ids: [],
  });
  const similarServices = form.name.trim().length >= 3 ? findSimilarServices(form.name, existingServices) : [];

  useEffect(() => {
    fetch(`${BASE}/api/users`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setUsers(Array.isArray(d) ? d : [])).catch(() => {});
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!canCreate) return deny('create');
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/services`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...form, invoice_description: form.description, auto_task_frequency: form.service_type === 'recurring' ? (form.auto_task_frequency || 'none') : 'none' })
      });
      if (res.ok) { showToast('Service created', 'success'); onSuccess(); }
      else { const d = await res.json(); showToast(d.message || 'Error', 'error'); }
    } catch (e) { showToast('Server error', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:16 }}>
      <style>{`.no-scroll::-webkit-scrollbar{display:none}`}</style>
      <div style={{ background:T.bg2, borderRadius:20, width:'100%', maxWidth:1100, maxHeight:'90vh', display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'20px 28px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <h2 style={{ fontSize:17, fontWeight:700, color:T.text, margin:0 }}>New Service</h2>
          <button type="button" onClick={onClose} style={{ width:32, height:32, borderRadius:'50%', border:'none', background:T.bg3, color:T.text3, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
        </div>

        <div style={{ display:'flex', borderBottom:`1px solid ${T.border}`, padding:'0 28px', flexShrink:0 }}>
          {['basic', 'billing'].map(tab => (
            <button key={tab} type="button" onClick={() => setActiveTab(tab)} style={{
              padding:'12px 16px', fontSize:13, fontWeight:600, border:'none', background:'none',
              borderBottom: activeTab===tab ? '2px solid #3b82f6' : '2px solid transparent',
              color: activeTab===tab ? '#3b82f6' : T.text3, cursor:'pointer', whiteSpace:'nowrap',
            }}>
              {tab==='basic' ? 'Basic Details' : 'Billing Settings'}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="no-scroll" style={{ flex:1, minHeight:0, overflowY:'auto', padding:28, scrollbarWidth:'none', msOverflowStyle:'none' }}>
          <div style={{ maxWidth:640 }}>
            {activeTab === 'basic' && (
              <ServiceBasicDetailsFields form={form} setForm={setForm} users={users} categories={categories}
                onCategoryCreated={onCategoryCreated} token={token} T={T} darkMode={darkMode} showServiceCode={false}
                nameHint={similarServices.length > 0 && (
                  <div style={{ marginTop:6, padding:'8px 10px', borderRadius:8, background:'#fffbeb', border:'1px solid #fde68a' }}>
                    <p style={{ fontSize:11.5, color:'#92400e', margin:0, lineHeight:1.5 }}>
                      Similar service{similarServices.length > 1 ? 's' : ''} already exist{similarServices.length === 1 ? 's' : ''} — is this different?{' '}
                      <strong>{similarServices.map(s => s.name).join(', ')}</strong>. You can still save this as a new, unique service.
                    </p>
                  </div>
                )} />
            )}
            {activeTab === 'billing' && (
              <ServiceBillingSettingsFields form={form} setForm={setForm} T={T} darkMode={darkMode} />
            )}
          </div>
        </form>

        <div style={{ display:'flex', gap:12, padding:'16px 28px', borderTop:`1px solid ${T.border}`, flexShrink:0 }}>
          <button type="button" onClick={onClose} style={{ flex:1, padding:'11px 0', borderRadius:10, border:`1px solid ${T.border2}`, background:'none', color:T.text2, fontSize:13, fontWeight:600, cursor:'pointer' }}>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={saving} style={{
            flex:1, padding:'11px 0', borderRadius:10, border:'none',
            background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600,
            cursor:'pointer', opacity:saving?0.7:1,
          }}>
            {saving ? 'Saving...' : 'Add Service'}
          </button>
        </div>
      </div>
    </div>
  );
}

// SERVICE DETAIL MODAL — Full Edit with Tabs
function ServiceDetailModal({ service, token, onClose, onSuccess, darkMode = false, T: theme, existingServices = [], categories = [], onCategoryCreated }) {
  const { showToast } = useToast();
  const { canCreate, canEdit, canDelete, deny } = useServiceAccess(showToast);
  const [confirmD, setConfirmD] = useState(null);
  const showConfirmD = (title, message, onConfirm) => setConfirmD({ title, message, onConfirm });
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [activeTab, setActiveTab] = useState('settings');
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    name: service.name || '',
    service_code: service.service_code || '',
    category: service.category || 'Other',
    service_type: service.service_type || 'one_time',
    billing_frequency: service.billing_frequency || '',
    default_amount: service.default_amount || '',
    gst_percent: service.gst_percent == null || service.gst_percent === '' ? 18 : Number(service.gst_percent),
    sac_code: service.sac_code || '',
    description: service.description || service.invoice_description || '',
    status: service.status || 'active',
    is_checklist_required: service.is_checklist_required || false,
    is_billable_by_default: service.is_billable_by_default !== false,
    auto_task_frequency: (service.auto_task_frequency && service.auto_task_frequency !== 'none') ? service.auto_task_frequency : '',
    auto_assign_to: service.auto_assign_to || '',
    auto_task_advance_days: service.auto_task_advance_days || 7,
    auto_task_creation_day: service.auto_task_creation_day || 1,
    auto_task_due_day: service.auto_task_due_day || 11,
    auto_task_target_offset_days: service.auto_task_target_offset_days ?? 9,
    auto_task_period_mode: service.auto_task_period_mode || 'recently_completed',
    auto_assign_user_ids: Array.isArray(service.auto_assign_user_ids) ? service.auto_assign_user_ids.map(String) : [],
    auto_assign_to_clients: service.auto_assign_to_clients || false,
    auto_task_creation_month_offset: service.auto_task_creation_month_offset || 0,
    auto_task_due_month_offset: service.auto_task_due_month_offset || 0,
    default_assign_user_ids: Array.isArray(service.default_assign_user_ids) ? service.default_assign_user_ids.map(String) : [],
    referral_commission_type: service.referral_commission_type || 'fixed',
    referral_commission_value: service.referral_commission_value || '',
    referral_commission_notes: service.referral_commission_notes || '',
  });

  const [checklist, setChecklist] = useState([]);
  const [subtasks, setSubtasks] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [newStep, setNewStep] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [clientCount, setClientCount] = useState(0);

  useEffect(() => {
    fetchChecklist();
    fetchSubtasks();
    fetchCustomFields();
    fetch(`${BASE}/api/users`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setUsers(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${BASE}/api/services/${service.id}/clients`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setClientCount(Array.isArray(d) ? d.length : 0)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchChecklist = async () => {
    try {
      const res = await fetch(`${BASE}/api/services/${service.id}/checklist`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setChecklist(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  const fetchSubtasks = async () => {
    try {
      const res = await fetch(`${BASE}/api/services/${service.id}/subtasks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSubtasks(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  const fetchCustomFields = async () => {
    try {
      const res = await fetch(`${BASE}/api/services/${service.id}/custom-fields`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setCustomFields(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  const handleSaveSettings = async () => {
    if (!canEdit) return deny('edit');
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/services/${service.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...form, invoice_description: form.description, auto_task_frequency: form.service_type === 'recurring' ? (form.auto_task_frequency || 'none') : 'none' })
      });
      if (res.ok) { showToast('Settings saved!', 'success'); onSuccess(); }
    } catch (e) { showToast('Error saving', 'error'); }
    finally { setSaving(false); }
  };

  const handleAddChecklistStep = async () => {
    if (!newStep.trim()) return;
    if (!canCreate) return deny('create');
    try {
      await fetch(`${BASE}/api/services/${service.id}/checklist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ step_name: newStep, step_order: checklist.length })
      });
      setNewStep('');
      fetchChecklist();
    } catch (e) { console.error(e); }
  };

  const handleDeleteChecklistStep = async (stepId) => {
    if (!canDelete) return deny('delete');
    try {
      await fetch(`${BASE}/api/services/${service.id}/checklist/${stepId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchChecklist();
    } catch (e) { console.error(e); }
  };

  const handleUpdateChecklistStep = async (stepId, value) => {
    if (!canEdit) return;
    setChecklist(prev => prev.map(s => s.id === stepId ? { ...s, step_name: value } : s));
  };

  const handleSaveChecklist = async () => {
    if (!canEdit) return deny('edit');
    try {
      for (const step of checklist) {
        await fetch(`${BASE}/api/services/${service.id}/checklist/${step.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ step_name: step.step_name })
        });
      }
      showToast('Checklist saved!', 'success');
    } catch (e) { console.error(e); }
  };

  const handleAddSubtask = async () => {
    if (!canCreate) return deny('create');
    try {
      const res = await fetch(`${BASE}/api/services/${service.id}/subtasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          subtask_name: 'New Subtask',
          description: '',
          due_date_offset: 0,
          target_date_offset: 0,
          subtask_order: subtasks.length
        })
      });
      const newSubtask = await res.json();
      setSubtasks(prev => [...prev, newSubtask]);
    } catch (e) { console.error(e); }
  };

  const handleUpdateSubtask = (id, field, value) => {
    if (!canEdit) return;
    setSubtasks(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const handleSaveSubtasks = async () => {
    if (!canEdit) return deny('edit');
    try {
      for (const subtask of subtasks) {
        await fetch(`${BASE}/api/services/${service.id}/subtasks/${subtask.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(subtask)
        });
      }
      showToast('Subtasks saved!', 'success');
    } catch (e) { console.error(e); }
  };

  const handleDeleteSubtask = async (id) => {
    if (!canDelete) return deny('delete');
    try {
      await fetch(`${BASE}/api/services/${service.id}/subtasks/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchSubtasks();
    } catch (e) { console.error(e); }
  };

  const handleAddCustomField = async () => {
    if (!canCreate) return deny('create');
    try {
      const res = await fetch(`${BASE}/api/services/${service.id}/custom-fields`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ field_name: 'New Field', field_type: 'text', is_required: false })
      });
      const newField = await res.json();
      setCustomFields(prev => [...prev, newField]);
    } catch (e) { console.error(e); }
  };

  const handleDeleteCustomField = async (id) => {
    if (!canDelete) return deny('delete');
    try {
      await fetch(`${BASE}/api/services/${service.id}/custom-fields/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchCustomFields();
    } catch (e) { console.error(e); }
  };

  const handleUpdateCustomField = (id, field, value) => {
    if (!canEdit) return;
    setCustomFields(prev => prev.map(f => f.id === id ? { ...f, [field]: value } : f));
  };

  const handleSaveCustomFields = async () => {
    if (!canEdit) return deny('edit');
    try {
      for (const field of customFields) {
        await fetch(`${BASE}/api/services/${service.id}/custom-fields/${field.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(field)
        });
      }
      showToast('Custom fields saved!', 'success');
    } catch (e) { console.error(e); }
  };

  const handleDelete = () => {
    if (!canDelete) return deny('delete');
    showConfirmD('Delete Service', `Delete service "${service.name}"? This cannot be undone.`, async () => {
      setDeleting(true);
      try {
        const res = await fetch(`${BASE}/api/services/${service.id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) { showToast('Service deleted', 'success'); onSuccess(); }
        else { const d = await res.json().catch(() => ({})); showToast(d.message || 'Error deleting', 'error'); }
      } catch (e) { showToast('Error deleting', 'error'); }
      finally { setDeleting(false); }
    });
  };

  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500";
  const icStyle = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:16 }}>
      <ConfirmModal open={!!confirmD} title={confirmD?.title||''} message={confirmD?.message||''} onConfirm={()=>{confirmD?.onConfirm();setConfirmD(null);}} onCancel={()=>setConfirmD(null)} />
      <style>{`.svc-no-scroll::-webkit-scrollbar{display:none}`}</style>
      <div className="svc-no-scroll" style={{ background:T.bg2, borderRadius:20, width:'100%', maxWidth:900, maxHeight:'95vh', overflowY:'auto', scrollbarWidth:'none', msOverflowStyle:'none', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>

        {/* Header */}
        <div style={{ padding:'20px 24px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:T.bg2, zIndex:10 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, flex:1 }}>
            {editingName ? (
              <div style={{ flex:1, position:'relative' }}>
                <input type="text" value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  onBlur={() => setEditingName(false)} autoFocus
                  style={{ fontSize:17, fontWeight:700, borderBottom:`2px solid #3b82f6`, outline:'none', width:'100%', background:'transparent', color:T.text }} />
                {(() => {
                  const similar = form.name.trim().length >= 3 ? findSimilarServices(form.name, existingServices.filter(s => s.id !== service.id)) : [];
                  return similar.length > 0 ? (
                    <div onMouseDown={e => e.preventDefault()} style={{ position:'absolute', top:'100%', left:0, marginTop:4, padding:'6px 10px', borderRadius:8, background:'#fffbeb', border:'1px solid #fde68a', zIndex:5, width:'max-content', maxWidth:340 }}>
                      <p style={{ fontSize:11, color:'#92400e', margin:0, lineHeight:1.5 }}>Similar: <strong>{similar.map(s => s.name).join(', ')}</strong></p>
                    </div>
                  ) : null;
                })()}
              </div>
            ) : (
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <h2 style={{ fontSize:17, fontWeight:700, color:T.text, margin:0 }}>{form.name}</h2>
                {service.service_code && (
                  <span style={{ fontSize:11, fontFamily:'monospace', fontWeight:600, color:'#3b82f6', background: darkMode?'rgba(59,130,246,0.15)':'#eff6ff', border:`1px solid ${darkMode?'rgba(59,130,246,0.3)':'#bfdbfe'}`, padding:'2px 8px', borderRadius:6 }}>
                    {service.service_code}
                  </span>
                )}
                {canEdit && <button onClick={() => setEditingName(true)}
                  style={{ width:26, height:26, borderRadius:7, background:'none', border:`1px solid ${T.border2}`, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:T.text3 }} title="Edit name">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </button>}
              </div>
            )}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {canDelete && <button onClick={handleDelete} disabled={deleting} style={{
              display:'flex', alignItems:'center', gap:7,
              padding:'8px 16px', borderRadius:9,
              background: deleting?'#7f1d1d':'linear-gradient(135deg,#dc2626,#b91c1c)',
              border:'none', color:'#fff', fontSize:12, fontWeight:600,
              cursor: deleting?'not-allowed':'pointer',
              boxShadow: deleting?'none':'0 3px 10px rgba(220,38,38,0.35)',
              opacity: deleting?0.7:1, transition:'all 0.2s',
            }}
            onMouseEnter={e => { if(!deleting){ e.currentTarget.style.boxShadow='0 5px 16px rgba(220,38,38,0.5)'; e.currentTarget.style.transform='translateY(-1px)'; }}}
            onMouseLeave={e => { e.currentTarget.style.boxShadow='0 3px 10px rgba(220,38,38,0.35)'; e.currentTarget.style.transform='translateY(0)'; }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
              </svg>
              {deleting ? 'Deleting...' : 'Delete'}
            </button>}
            <button onClick={onClose} style={{ width:32, height:32, borderRadius:8, border:`1px solid ${T.border2}`, background:T.bg3, color:T.text3, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', borderBottom:`1px solid ${T.border}`, padding:'0 24px', background:T.bg2 }}>
          {['settings','checklist','subtasks','custom_fields',...(form.service_type === 'recurring' ? ['clients'] : []),'supporting_files'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              padding:'12px 16px', fontSize:13, fontWeight:600, border:'none', background:'none',
              borderBottom: activeTab===tab ? '2px solid #3b82f6' : '2px solid transparent',
              color: activeTab===tab ? '#3b82f6' : T.text3,
              cursor:'pointer', textTransform:'capitalize', transition:'all 0.15s', whiteSpace:'nowrap',
            }}>
              {tab==='custom_fields'?'Custom Fields':tab==='supporting_files'?'Supporting Files':tab==='clients'?`Clients (${clientCount})`:tab.charAt(0).toUpperCase()+tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ padding:24, minHeight:450, background:T.bg2 }}>

          {/* SETTINGS TAB */}
          {activeTab === 'settings' && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>
              {/* Basic Details */}
              <div>
                <h3 style={{ fontWeight:700, fontSize:13, color:T.text, marginBottom:14 }}>Basic Details</h3>
                <fieldset disabled={!canEdit} style={{ border:0, margin:0, padding:0, minWidth:0 }}>
                  <ServiceBasicDetailsFields form={form} setForm={setForm} users={users} categories={categories}
                    onCategoryCreated={onCategoryCreated} token={token} T={T} darkMode={darkMode} />
                </fieldset>
              </div>

              {/* Billing Settings */}
              <div>
                <h3 style={{ fontWeight:700, fontSize:13, color:T.text, marginBottom:14 }}>Billing Settings</h3>
                <fieldset disabled={!canEdit} style={{ border:0, margin:0, padding:0, minWidth:0 }}>
                  <ServiceBillingSettingsFields form={form} setForm={setForm} T={T} darkMode={darkMode} />
                </fieldset>
              </div>

              {/* Commission Structure */}
              <fieldset disabled={!canEdit} style={{ gridColumn:'span 2', border:0, margin:0, padding:0, minWidth:0 }}>
              <div style={{ border:`1px solid ${darkMode?'rgba(249,115,22,0.3)':'#fed7aa'}`, borderRadius:12, padding:18, background:darkMode?'rgba(249,115,22,0.06)':'#fff7ed' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
                  <div style={{ width:28, height:28, borderRadius:7, background:darkMode?'rgba(249,115,22,0.15)':'#ffedd5', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2"><line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>
                  </div>
                  <p style={{ fontSize:12, fontWeight:700, color:'#f97316', margin:0 }}>Referral Commission Structure</p>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14 }}>
                  <div>
                    <label style={{ display:'block', fontSize:11, fontWeight:600, color:darkMode?'#fdba74':'#92400e', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Commission Type</label>
                    <select value={form.referral_commission_type} onChange={e => setForm(f => ({ ...f, referral_commission_type: e.target.value }))} style={icStyle}>
                      <option value="fixed">Fixed Amount (₹)</option>
                      <option value="percentage">Percentage (%)</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:11, fontWeight:600, color:darkMode?'#fdba74':'#92400e', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                      {form.referral_commission_type === 'percentage' ? 'Rate (%)' : 'Amount (₹)'}
                    </label>
                    <input type="number" min={0}
                      value={form.referral_commission_value}
                      onChange={e => setForm(f => ({ ...f, referral_commission_value: e.target.value }))}
                      placeholder={form.referral_commission_type === 'percentage' ? 'e.g. 10' : 'e.g. 500'}
                      style={icStyle} />
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:11, fontWeight:600, color:darkMode?'#fdba74':'#92400e', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Notes</label>
                    <input type="text"
                      value={form.referral_commission_notes}
                      onChange={e => setForm(f => ({ ...f, referral_commission_notes: e.target.value }))}
                      placeholder="e.g. Only for new clients"
                      style={icStyle} />
                  </div>
                </div>
                {Number(form.referral_commission_value) > 0 && (
                  <div style={{ marginTop:10, padding:'8px 12px', background:darkMode?'rgba(249,115,22,0.1)':'#ffedd5', borderRadius:8 }}>
                    <p style={{ fontSize:12, color:'#ea580c', margin:0, fontWeight:600 }}>
                      Preview: {form.referral_commission_type === 'percentage'
                        ? `${form.referral_commission_value}% of invoice${form.default_amount ? ` = ₹${(Number(form.default_amount) * Number(form.referral_commission_value) / 100).toLocaleString('en-IN')}` : ''}`
                        : `₹${Number(form.referral_commission_value).toLocaleString('en-IN')} fixed per referred task`}
                    </p>
                  </div>
                )}
              </div>
              </fieldset>

              {canEdit && (
              <div style={{ gridColumn:'span 2', display:'flex', justifyContent:'flex-end', paddingTop:16, borderTop:`1px solid ${T.border}` }}>
                <button onClick={handleSaveSettings} disabled={saving} style={{
                  padding:'10px 28px', borderRadius:10, border:'none',
                  background:'linear-gradient(135deg,#0f1f45,#1a3a6b)',
                  color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer',
                  boxShadow:'0 4px 12px rgba(15,31,69,0.3)', opacity: saving?0.7:1,
                }}>
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
              )}
            </div>
          )}

          {/* CHECKLIST TAB */}
          {activeTab === 'checklist' && (
            <div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                <div>
                  <h3 style={{ fontWeight:700, fontSize:13, color:T.text, margin:0 }}>Document Checklist</h3>
                  <p style={{ fontSize:11, color:T.text3, marginTop:3 }}>Documents required from client for this service</p>
                </div>
                {canEdit && <button onClick={handleSaveChecklist} style={{ padding:'6px 16px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                  Save Checklist
                </button>}
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:14 }}>
                {checklist.length === 0 ? (
                  <p style={{ textAlign:'center', color:T.text3, padding:'24px 0', fontSize:13 }}>No checklist items yet. Add steps below.</p>
                ) : checklist.map((step, index) => (
                  <div key={step.id} style={{ display:'flex', alignItems:'center', gap:10, background:T.bg3, border:`1px solid ${T.border}`, borderRadius:9, padding:'10px 12px' }}>
                    <span style={{ color:T.text3, cursor:'grab', userSelect:'none', fontSize:16 }}>≡</span>
                    <span style={{ fontSize:11, color:T.text3, width:20 }}>{index+1}.</span>
                    <input type="text" value={step.step_name} readOnly={!canEdit}
                      onChange={e => handleUpdateChecklistStep(step.id, e.target.value)}
                      style={{ flex:1, background:'transparent', border:'none', borderBottom:`1px solid ${T.border2}`, outline:'none', fontSize:13, color:T.text, padding:'2px 0' }} />
                    {canDelete && <button onClick={() => handleDeleteChecklistStep(step.id)}
                      style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', display:'flex', alignItems:'center', padding:4, borderRadius:6 }}
                      onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2'}
                      onMouseLeave={e => e.currentTarget.style.background='none'}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                    </button>}
                  </div>
                ))}
              </div>
              {canCreate && <div style={{ display:'flex', gap:8 }}>
                <input type="text" placeholder="Add new checklist item..."
                  value={newStep} onChange={e => setNewStep(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleAddChecklistStep()}
                  style={{ flex:1, border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'9px 14px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }} />
                <button onClick={handleAddChecklistStep} style={{ padding:'9px 18px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                  + Add Step
                </button>
              </div>}
            </div>
          )}

          {/* SUBTASKS TAB */}
          {activeTab === 'subtasks' && (
            <div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                <div>
                  <h3 style={{ fontWeight:700, fontSize:13, color:T.text, margin:0 }}>Workflow Subtasks</h3>
                  <p style={{ fontSize:11, color:T.text3, marginTop:3 }}>Internal steps for staff when working on this service</p>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  {canCreate && <button onClick={handleAddSubtask} style={{ padding:'6px 14px', borderRadius:8, border:`1.5px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:12, fontWeight:600, cursor:'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor='#f97316'; e.currentTarget.style.color='#f97316'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor=T.border2; e.currentTarget.style.color=T.text2; }}>
                    + Add Subtask
                  </button>}
                  {canEdit && <button onClick={handleSaveSubtasks} style={{ padding:'6px 16px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                    Save Subtasks
                  </button>}
                </div>
              </div>
              {subtasks.length === 0 ? (
                <p style={{ textAlign:'center', color:T.text3, padding:'32px 0', fontSize:13 }}>No subtasks yet. Click "+ Add Subtask" to create workflow steps.</p>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                  {subtasks.map((subtask, index) => (
                    <div key={subtask.id} style={{ border:`1px solid ${T.border}`, borderRadius:12, padding:16, background:T.bg3 }}>
                      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, marginBottom:10 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, flex:1 }}>
                          <span style={{ color:T.text3, cursor:'grab', fontSize:16 }}>≡</span>
                          <span style={{ fontSize:11, fontWeight:700, color:T.text3 }}>{index+1}</span>
                          <input type="text" placeholder="Subtask title" readOnly={!canEdit}
                            value={subtask.subtask_name}
                            onChange={e => handleUpdateSubtask(subtask.id, 'subtask_name', e.target.value)}
                            style={{ flex:1, fontSize:13, fontWeight:600, color:T.text, background:'transparent', border:'none', borderBottom:`1px solid ${T.border2}`, outline:'none', padding:'2px 0' }} />
                        </div>
                        {canDelete && <button onClick={() => handleDeleteSubtask(subtask.id)}
                          style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:4, borderRadius:6, display:'flex', alignItems:'center' }}
                          onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2'}
                          onMouseLeave={e => e.currentTarget.style.background='none'}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                        </button>}
                      </div>
                      <textarea rows="2" placeholder="Description / instructions for staff..." readOnly={!canEdit}
                        value={subtask.description || ''}
                        onChange={e => handleUpdateSubtask(subtask.id, 'description', e.target.value)}
                        style={{ width:'100%', fontSize:13, color:T.text2, border:`1px solid ${T.border}`, borderRadius:8, padding:'8px 10px', background:T.inputBg, outline:'none', resize:'none', marginBottom:10, boxSizing:'border-box' }} />
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
                        {[
                          { label:'Due Date (days after task start)', field:'due_date_offset', type:'number' },
                          { label:'Target Date (days after task start)', field:'target_date_offset', type:'number' },
                        ].map(f => (
                          <div key={f.field}>
                            <label style={{ display:'block', fontSize:11, color:T.text3, marginBottom:4 }}>{f.label}</label>
                            <input type={f.type} placeholder="0" value={subtask[f.field] || 0} readOnly={!canEdit}
                              onChange={e => handleUpdateSubtask(subtask.id, f.field, e.target.value)}
                              style={{ width:'100%', border:`1px solid ${T.border}`, borderRadius:8, padding:'6px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' }} />
                          </div>
                        ))}
                        <div>
                          <label style={{ display:'block', fontSize:11, color:T.text3, marginBottom:4 }}>Assign to Role</label>
                          <select value={subtask.assigned_role || ''} disabled={!canEdit} onChange={e => handleUpdateSubtask(subtask.id, 'assigned_role', e.target.value)}
                            style={{ width:'100%', border:`1px solid ${T.border}`, borderRadius:8, padding:'6px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' }}>
                            <option value="">Any</option>
                            <option value="admin">Admin</option>
                            <option value="manager">Manager</option>
                            <option value="staff">Staff</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* CUSTOM FIELDS TAB */}
          {activeTab === 'custom_fields' && (
            <div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                <div>
                  <h3 style={{ fontWeight:700, fontSize:13, color:T.text, margin:0 }}>Custom Fields</h3>
                  <p style={{ fontSize:11, color:T.text3, marginTop:3 }}>Extra fields that appear when creating a task for this service</p>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  {canCreate && <button onClick={handleAddCustomField} style={{ padding:'6px 14px', borderRadius:8, border:`1.5px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:12, fontWeight:600, cursor:'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor='#f97316'; e.currentTarget.style.color='#f97316'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor=T.border2; e.currentTarget.style.color=T.text2; }}>
                    + Add Field
                  </button>}
                  {canEdit && <button onClick={handleSaveCustomFields} style={{ padding:'6px 16px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                    Save Fields
                  </button>}
                </div>
              </div>
              {customFields.length === 0 ? (
                <p style={{ textAlign:'center', color:T.text3, padding:'32px 0', fontSize:13 }}>No custom fields yet.</p>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {customFields.map(field => (
                    <div key={field.id} style={{ display:'flex', alignItems:'center', gap:12, border:`1px solid ${T.border}`, borderRadius:12, padding:16, background:T.bg3 }}>
                      <div style={{ flex:1, display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                        <div>
                          <label style={{ display:'block', fontSize:11, color:T.text3, marginBottom:4 }}>Field Name</label>
                          <input type="text" value={field.field_name} readOnly={!canEdit}
                            onChange={e => handleUpdateCustomField(field.id, 'field_name', e.target.value)}
                            style={{ width:'100%', border:`1px solid ${T.border}`, borderRadius:8, padding:'6px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' }} />
                        </div>
                        <div>
                          <label style={{ display:'block', fontSize:11, color:T.text3, marginBottom:4 }}>Field Type</label>
                          <select value={field.field_type} disabled={!canEdit} onChange={e => handleUpdateCustomField(field.id, 'field_type', e.target.value)}
                            style={{ width:'100%', border:`1px solid ${T.border}`, borderRadius:8, padding:'6px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' }}>
                            <option value="text">Text</option>
                            <option value="number">Number</option>
                            <option value="date">Date</option>
                            <option value="dropdown">Dropdown</option>
                            <option value="checkbox">Checkbox</option>
                          </select>
                        </div>
                        <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:2 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span style={{ fontSize:12, color:T.text2 }}>Required</span>
                            <button type="button" onClick={() => handleUpdateCustomField(field.id, 'is_required', !field.is_required)}
                              style={{ position:'relative', width:32, height:17, borderRadius:9, border:'none', cursor:'pointer', background: field.is_required?'#3b82f6':(darkMode?'#334155':'#d1d5db'), transition:'background 0.2s', flexShrink:0 }}>
                              <span style={{ position:'absolute', top:2, left: field.is_required?17:2, width:13, height:13, borderRadius:'50%', background:'#fff', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }}/>
                            </button>
                          </div>
                        </div>
                      </div>
                      <button onClick={() => handleDeleteCustomField(field.id)}
                        style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:6, borderRadius:6, display:'flex', alignItems:'center', flexShrink:0 }}
                        onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2'}
                        onMouseLeave={e => e.currentTarget.style.background='none'}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
           {/* CLIENTS TAB */}
          {activeTab === 'clients' && form.service_type === 'recurring' && (
            <ServiceClientsTab serviceId={service.id} token={token} darkMode={darkMode} T={T}
              onCountChange={setClientCount} canRun={!!form.auto_task_frequency} />
          )}

           {/* SUPPORTING FILES TAB */}
          {activeTab === 'supporting_files' && (
            <SupportingFilesTab serviceId={service.id} token={token} darkMode={darkMode} T={T} />
          )}

          </div>
      </div>
    </div>
  );
}

function ReferralCommissionTab({ service, token, darkMode = false, T: theme }) {
  const { showToast } = useToast();
  const { canEdit, deny } = useServiceAccess(showToast);
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };

  const [form, setForm] = useState({
    default_billing_amount: service.default_billing_amount || '',
    referral_commission_type: service.referral_commission_type || 'fixed',
    referral_commission_value: service.referral_commission_value || '',
    referral_commission_notes: service.referral_commission_notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    if (!canEdit) return deny('edit');
    setSaving(true);
    try {
      await fetch(`${BASE}/api/services/${service.id}/referral-commission`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(form),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (_) {}
    setSaving(false);
  };

  const preview = () => {
    const v = Number(form.referral_commission_value || 0);
    const b = Number(form.default_billing_amount || 0);
    if (!v) return '—';
    if (form.referral_commission_type === 'percentage') {
      return b ? `₹${((b * v) / 100).toLocaleString('en-IN')} on ₹${b.toLocaleString('en-IN')} billing` : `${v}% of invoice`;
    }
    return `₹${v.toLocaleString('en-IN')} fixed per task`;
  };

  return (
    <div style={{ maxWidth: 520 }}>
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ fontSize: 14, fontWeight: 700, color: T.text, margin: '0 0 4px' }}>Referral Commission Settings</h4>
        <p style={{ fontSize: 12, color: T.text3, margin: 0 }}>Commission earned when referred task is fully paid</p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* Default Billing Amount */}
        <div style={{ background: T.bg3, border: `1px solid ${T.border}`, borderRadius: 12, padding: 18 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Default Billing Amount (₹)</label>
          <input type="number" min={0} value={form.default_billing_amount}
            onChange={e => setForm(f => ({ ...f, default_billing_amount: e.target.value }))}
            placeholder="e.g. 5000" style={is} />
          <p style={{ fontSize: 11, color: T.text3, marginTop: 6 }}>Auto-filled when task is created for this service</p>
        </div>

        {/* Commission Settings */}
        <div style={{ background: T.bg3, border: `1px solid ${T.border}`, borderRadius: 12, padding: 18 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Commission Structure</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 5 }}>Type</label>
              <select value={form.referral_commission_type} onChange={e => setForm(f => ({ ...f, referral_commission_type: e.target.value }))} style={is}>
                <option value="fixed">Fixed Amount (₹)</option>
                <option value="percentage">Percentage (%)</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 5 }}>
                {form.referral_commission_type === 'percentage' ? 'Rate (%)' : 'Amount (₹)'}
              </label>
              <input type="number" min={0} value={form.referral_commission_value}
                onChange={e => setForm(f => ({ ...f, referral_commission_value: e.target.value }))}
                placeholder={form.referral_commission_type === 'percentage' ? 'e.g. 10' : 'e.g. 500'} style={is} />
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 5 }}>Notes (optional)</label>
            <input type="text" value={form.referral_commission_notes}
              onChange={e => setForm(f => ({ ...f, referral_commission_notes: e.target.value }))}
              placeholder="e.g. Only for new clients" style={is} />
          </div>
        </div>

        {/* Preview */}
        {(form.referral_commission_value > 0) && (
          <div style={{ background: darkMode ? 'rgba(249,115,22,0.1)' : '#fff7ed', border: `1px solid ${darkMode ? 'rgba(249,115,22,0.3)' : '#fed7aa'}`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2"><line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>
            <div>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#f97316', margin: 0 }}>Commission Preview</p>
              <p style={{ fontSize: 13, color: darkMode ? '#fdba74' : '#c2410c', margin: '3px 0 0' }}>{preview()}</p>
            </div>
          </div>
        )}

        {/* Save Button */}
        {canEdit && <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={handleSave} disabled={saving} style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving...' : 'Save Commission Settings'}
          </button>
          {saved && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#16a34a', fontWeight: 600 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              Saved
            </span>
          )}
        </div>}
      </div>
    </div>
  );
}
// CLIENTS TAB — which clients this service's recurring auto-tasks should be generated for.
// The full client list (GET /api/clients) is a heavy join (~230 KB for a big firm). It is only
// needed once the user starts searching to enroll someone, so it is fetched lazily on first
// focus of the search box and cached for a minute — opening the tab must never wait on it.
let svcClientListCache = { at: 0, token: null, rows: null };

function ServiceClientsTab({ serviceId, token, darkMode = false, T: theme, onCountChange, canRun = false }) {
  const { showToast } = useToast();
  const { canEdit, canIn, deny } = useServiceAccess(showToast);
  // Running generation creates Tasks, so it needs tasks.create on top of services.edit.
  const canRunNow = canEdit && canIn('tasks', 'create');
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8' };
  const [enrolled, setEnrolled] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [addSearch, setAddSearch] = useState('');
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [running, setRunning] = useState(false);

  const handleRunNow = async () => {
    if (!canEdit) return deny('edit');
    if (!canIn('tasks', 'create')) {
      showToast("You don't have permission to create tasks. Ask your admin.", 'error');
      return;
    }
    setRunning(true);
    try {
      const res = await fetch(`${BASE}/api/tasks/create-recurring`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ignoreCreationDay: true, serviceId }),
      });
      const data = await res.json();
      if (res.ok) showToast(`Created ${data.created} task(s) for this service`, data.created ? 'success' : 'info');
      else showToast(data.message || 'Error running generation', 'error');
    } catch (e) { showToast('Server error', 'error'); }
    finally { setRunning(false); }
  };

  const fetchEnrolled = () => {
    fetch(`${BASE}/api/services/${serviceId}/clients`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => { const rows = Array.isArray(d) ? d : []; setEnrolled(rows); onCountChange?.(rows.length); })
      .catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchEnrolled(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAllClients = () => {
    if (clientsLoading || allClients.length) return;
    const c = svcClientListCache;
    if (c.rows && c.token === token && Date.now() - c.at < 60000) { setAllClients(c.rows); return; }
    setClientsLoading(true);
    fetch(`${BASE}/api/clients`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const rows = (Array.isArray(d) ? d : (Array.isArray(d?.clients) ? d.clients : []))
          .filter(x => x.is_active !== false)
          .map(x => ({ id: x.id, client_name: x.client_name }));
        svcClientListCache = { at: Date.now(), token, rows };
        setAllClients(rows);
      })
      .catch(() => {})
      .finally(() => setClientsLoading(false));
  };

  const handleAdd = async (clientId) => {
    if (!canEdit) return deny('edit');
    try {
      const res = await fetch(`${BASE}/api/services/${serviceId}/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ client_ids: [clientId] }),
      });
      if (res.ok) { fetchEnrolled(); showToast('Client enrolled', 'success'); }
      else showToast('Error enrolling client', 'error');
    } catch (e) { showToast('Server error', 'error'); }
    finally { setAddSearch(''); setShowAddDropdown(false); }
  };

  const handleRemove = async (clientId) => {
    if (!canEdit) return deny('edit');
    try {
      const res = await fetch(`${BASE}/api/services/${serviceId}/clients/${clientId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) { fetchEnrolled(); showToast('Client unenrolled', 'success'); }
      else showToast('Error removing client', 'error');
    } catch (e) { showToast('Server error', 'error'); }
  };

  const enrolledIds = new Set(enrolled.map(c => String(c.id)));
  const availableClients = allClients.filter(c => !enrolledIds.has(String(c.id)) &&
    (!addSearch || c.client_name?.toLowerCase().includes(addSearch.toLowerCase())));

  return (
    <div>
      <div style={{ marginBottom:16 }}>
        <h3 style={{ fontWeight:700, fontSize:13, color:T.text, margin:0 }}>Enrolled Clients</h3>
        <p style={{ fontSize:12, color:T.text3, marginTop:4 }}>Only these clients get auto-generated recurring tasks for this service.</p>
      </div>

      {canEdit && <div style={{ position:'relative', marginBottom:14 }}>
        <input type="text" placeholder="Search clients to add..." value={addSearch}
          onChange={e => { setAddSearch(e.target.value); setShowAddDropdown(true); }}
          onFocus={() => { setShowAddDropdown(true); loadAllClients(); }}
          onBlur={() => setTimeout(() => setShowAddDropdown(false), 150)}
          style={{ width:'100%', padding:'9px 12px', border:`1px solid ${T.border2}`, borderRadius:9, fontSize:13, outline:'none', background:T.bg2, color:T.text, boxSizing:'border-box' }} />
        {showAddDropdown && addSearch.trim() && (
          <div style={{ position:'absolute', left:0, right:0, top:'calc(100% + 6px)', background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.15)', zIndex:60, maxHeight:240, overflowY:'auto' }}>
            {clientsLoading && allClients.length === 0 ? (
              <p style={{ textAlign:'center', fontSize:12, color:T.text3, padding:'16px 0', margin:0 }}>Loading clients…</p>
            ) : availableClients.length === 0 ? (
              <p style={{ textAlign:'center', fontSize:12, color:T.text3, padding:'16px 0', margin:0 }}>No matching clients</p>
            ) : availableClients.map(c => (
              <button key={c.id} type="button" onMouseDown={() => handleAdd(c.id)}
                style={{ width:'100%', textAlign:'left', padding:'9px 14px', border:'none', cursor:'pointer', fontSize:13, fontWeight:500, background:'transparent', color:T.text }}
                onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                {c.client_name}
              </button>
            ))}
          </div>
        )}
      </div>}

      {loading ? (
        <p style={{ fontSize:13, color:T.text3, textAlign:'center', padding:'32px 0' }}>Loading...</p>
      ) : enrolled.length === 0 ? (
        <div style={{ textAlign:'center', color:T.text3, fontSize:13, padding:'40px 0', border:`2px dashed ${T.border2}`, borderRadius:12 }}>
          No clients enrolled yet — recurring tasks will not be created until you add some.
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {enrolled.map(c => (
            <div key={c.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:T.bg2, border:`1px solid ${T.border}`, borderRadius:10, padding:'10px 14px' }}>
              <span style={{ fontSize:13, fontWeight:500, color:T.text }}>{c.client_name}</span>
              {canEdit && <button type="button" onClick={() => handleRemove(c.id)}
                style={{ width:24, height:24, borderRadius:6, border:'none', background:'none', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2'}
                onMouseLeave={e => e.currentTarget.style.background='none'}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>}
            </div>
          ))}
        </div>
      )}

      {canRun && canRunNow && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, marginTop:16, padding:'12px 16px', borderRadius:10, background: darkMode?'rgba(59,130,246,0.08)':'#eff6ff', border:`1px solid ${darkMode?'rgba(59,130,246,0.2)':'#bfdbfe'}` }}>
          <p style={{ fontSize:11, color: darkMode?'#93c5fd':'#1d4ed8', margin:0 }}>
            {enrolled.length === 0
              ? 'Add at least one client above, then run generation.'
              : `Generate this period's tasks now for the ${enrolled.length} enrolled client(s).`}
          </p>
          <button type="button" onClick={handleRunNow} disabled={running || enrolled.length === 0}
            style={{ padding:'8px 16px', borderRadius:8, border:'none', background:'#3b82f6', color:'#fff', fontSize:12, fontWeight:600, cursor: (running || enrolled.length === 0) ? 'not-allowed' : 'pointer', opacity: (running || enrolled.length === 0) ? 0.55 : 1, flexShrink:0 }}>
            {running ? 'Running...' : 'Run Recurring Task Generation Now'}
          </button>
        </div>
      )}
    </div>
  );
}

function SupportingFilesTab({ serviceId, token, darkMode = false, T: theme }) {
  const { showToast } = useToast();
  const { canCreate, canDelete, deny } = useServiceAccess(showToast);
  const [confirmF, setConfirmF] = useState(null);
  const showConfirmF = (title, message, onConfirm) => setConfirmF({ title, message, onConfirm });
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8' };
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { fetchFiles(); }, []);

  const fetchFiles = async () => {
    try {
      const res = await fetch(`${BASE}/api/services/${serviceId}/files`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setFiles(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!canCreate) return deny('create');
    setUploading(true);
    try {
      await fetch(`${BASE}/api/services/${serviceId}/files`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          file_name: file.name,
          file_url: '#',
          file_size: `${(file.size / 1024).toFixed(1)} KB`
        })
      });
      fetchFiles();
    } catch (e) { console.error(e); }
    finally { setUploading(false); }
  };

  const handleDelete = (fileId) => {
    if (!canDelete) return deny('delete');
    showConfirmF('Delete File', 'Delete this file? This cannot be undone.', async () => {
      try {
        await fetch(`${BASE}/api/services/${serviceId}/files/${fileId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setFiles(prev => prev.filter(f => f.id !== fileId));
      } catch (e) { console.error(e); }
    });
  };

  return (
    <div>
      <ConfirmModal open={!!confirmF} title={confirmF?.title||''} message={confirmF?.message||''} onConfirm={()=>{confirmF?.onConfirm();setConfirmF(null);}} onCancel={()=>setConfirmF(null)} />
      <div style={{ marginBottom:16 }}>
        <h3 style={{ fontWeight:700, fontSize:13, color:T.text, margin:0 }}>Supporting Files</h3>
        <p style={{ fontSize:11, color:T.text3, marginTop:3 }}>Template documents staff can download when working on this service</p>
      </div>

      {/* Upload Area */}
      {canCreate && <div style={{ border:`2px dashed ${T.border2}`, borderRadius:12, padding:32, textAlign:'center', marginBottom:16, transition:'border-color 0.2s' }}
        onMouseEnter={e => e.currentTarget.style.borderColor='#3b82f6'}
        onMouseLeave={e => e.currentTarget.style.borderColor=T.border2}>
        <input type="file" id="service-file-upload" style={{ display:'none' }}
          onChange={handleUpload}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip,.csv,.ppt,.pptx" />
        <label htmlFor="service-file-upload" style={{ cursor:'pointer' }}>
          <div style={{ display:'flex', justifyContent:'center', marginBottom:12 }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          </div>
          <p style={{ fontSize:13, fontWeight:600, color:T.text2, margin:0 }}>
            {uploading ? 'Uploading...' : 'Drop files here or click to upload'}
          </p>
          <p style={{ fontSize:11, color:T.text3, marginTop:6 }}>PDF, DOC, XLS, PPT, JPG, ZIP — Max 10MB</p>
        </label>
      </div>}

      {/* Files List */}
      {files.length === 0 ? (
        <p style={{ textAlign:'center', color:T.text3, padding:'16px 0', fontSize:13 }}>No supporting files uploaded yet.</p>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {files.map(file => (
            <div key={file.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', border:`1px solid ${T.border}`, borderRadius:10, background:T.bg3, transition:'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background=T.bg2}
              onMouseLeave={e => e.currentTarget.style.background=T.bg3}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:34, height:34, borderRadius:8, background: darkMode?'rgba(59,130,246,0.12)':'#eff6ff', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                </div>
                <div>
                  <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>{file.file_name}</p>
                  <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>{file.file_size}</p>
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                {file.file_url !== '#' && (
                  <a href={file.file_url} target="_blank" rel="noreferrer"
                    style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, fontWeight:600, color:'#3b82f6', textDecoration:'none' }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    Download
                  </a>
                )}
                {canDelete && <button onClick={() => handleDelete(file.id)}
                  style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:6, borderRadius:6, display:'flex', alignItems:'center', gap:5, fontSize:12, fontWeight:500 }}
                  onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2'}
                  onMouseLeave={e => e.currentTarget.style.background='none'}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                  Delete
                </button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
// ── IMPORT SERVICES MODAL ────────────────────────────────────────────────────
function ImportServicesModal({ token, services, onClose, onSuccess, darkMode = false, T: theme }) {
  const { showToast } = useToast();
  const { canCreate, deny } = useServiceAccess(showToast);
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0', cardBorder:'#f1f5f9' };
  const [rows, setRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [stage, setStage] = useState('upload');
  const [parseError, setParseError] = useState('');
  const fileInputRef = useRef(null);

  const downloadTemplate = () => {
    const templateRows = [
      { 'Service ID': '', 'Service Name': 'GST Return Filing Monthly', 'Category': 'GST & Indirect Tax', 'Service Type': 'recurring', 'Billing Frequency': 'monthly', 'Default Rate (₹)': 2500, 'GST %': 18, 'SAC Code': '998231', 'Description': 'Monthly GST filing service', 'Invoice Description': '', 'Status': 'active' },
      { 'Service ID': '', 'Service Name': 'Income Tax Return Filing', 'Category': 'Income Tax & TDS', 'Service Type': 'one_time', 'Billing Frequency': '', 'Default Rate (₹)': 3000, 'GST %': 18, 'SAC Code': '998231', 'Description': 'Annual ITR filing', 'Invoice Description': '', 'Status': 'active' },
      { 'Service ID': '', 'Service Name': 'Company Incorporation', 'Category': 'Company Law (ROC/MCA)', 'Service Type': 'one_time', 'Billing Frequency': '', 'Default Rate (₹)': 15000, 'GST %': 18, 'SAC Code': '998313', 'Description': 'New company registration', 'Invoice Description': '', 'Status': 'active' },
    ];
    const ws = XLSX.utils.json_to_sheet(templateRows);
    ws['!cols'] = Object.keys(templateRows[0]).map(k => ({ wch: Math.max(k.length + 2, 20) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Services');
    XLSX.writeFile(wb, 'ChanakyaX_Services_Template.xlsx');
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!canCreate) return deny('create');
    setParseError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
        if (!data.length) { setParseError('No data rows found.'); return; }
        const parsed = data.map((row, idx) => {
          const category = String(row['Category'] || 'Other').trim();
          const sid = String(row['Service ID'] || '').trim();
          const name = String(row['Service Name'] || '').trim();
          return {
            _row: idx + 2, _error: !name ? 'Service Name required' : '',
            service_code: sid, name, category,
            service_type: String(row['Service Type'] || 'one_time').trim().toLowerCase(),
            billing_frequency: String(row['Billing Frequency'] || '').trim(),
            default_amount: row['Default Rate (₹)'] || '',
            gst_percent: row['GST %'] === '' || row['GST %'] == null ? 18 : Number(row['GST %']),
            sac_code: String(row['SAC Code'] || '').trim(),
            description: String(row['Description'] || '').trim(),
            invoice_description: String(row['Invoice Description'] || '').trim(),
            status: String(row['Status'] || 'active').trim().toLowerCase(),
          };
        });
        setRows(parsed);
        setStage('preview');
      } catch (err) { setParseError('Failed to parse file. Please use the template.'); }
    };
    reader.readAsBinaryString(file);
  };

  const handleImport = async () => {
    const valid = rows.filter(r => !r._error);
    if (!valid.length) return;
    if (!canCreate) return deny('create');
    setImporting(true);
    let ok = 0, fail = 0;
    for (const row of valid) {
      const { _row, _error, ...payload } = row;
      try {
        const res = await fetch(`${BASE}/api/services`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(payload)
        });
        if (res.ok) ok++; else fail++;
      } catch { fail++; }
    }
    setImporting(false);
    showToast(`Import complete: ${ok} added${fail ? `, ${fail} failed` : ''}.`, fail ? 'error' : 'success');
    onSuccess();
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:20, width:'100%', maxWidth: stage === 'upload' ? 580 : 900, maxHeight:'92vh', overflowY:'auto', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>

        {/* Header */}
        <div style={{ padding:'20px 28px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:T.bg2, zIndex:10 }}>
          <div>
            <h3 style={{ fontSize:17, fontWeight:700, color:T.text, margin:0 }}>
              {stage === 'upload' ? 'Import Services' : `Preview — ${rows.length} rows parsed`}
            </h3>
            <p style={{ fontSize:12, color:T.text3, marginTop:2 }}>{stage === 'upload' ? 'Bulk upload from Excel' : 'Review the rows before confirming import'}</p>
          </div>
          <button onClick={onClose} style={{ width:36, height:36, borderRadius:12, border:'none', background:'none', color:T.text3, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
            onMouseEnter={e => e.currentTarget.style.background = T.bg3}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Upload Stage */}
        {stage === 'upload' && (
          <div style={{ padding:28, display:'flex', flexDirection:'column', gap:20 }}>
            {/* Download Template card */}
            <div style={{ border:`1px solid ${T.border2}`, borderRadius:16, padding:20 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ width:40, height:40, borderRadius:12, background: darkMode?'rgba(16,185,129,0.12)':'#f0fdf4', border:`1px solid ${darkMode?'rgba(16,185,129,0.25)':'#bbf7d0'}`, color:'#16a34a', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  </div>
                  <div>
                    <p style={{ fontSize:14, fontWeight:600, color:T.text, margin:0 }}>Download Template</p>
                    <p style={{ fontSize:12, color:T.text3, margin:'2px 0 0' }}>Pre-formatted Excel with all columns + sample rows</p>
                  </div>
                </div>
                <button onClick={downloadTemplate} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 16px', borderRadius:12, border:`1px solid ${darkMode?'rgba(16,185,129,0.3)':'#bbf7d0'}`, background:'none', color:'#16a34a', fontSize:13, fontWeight:500, cursor:'pointer', flexShrink:0 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  Download
                </button>
              </div>
            </div>

            {/* Upload card */}
            <div style={{ border:`1px solid ${T.border2}`, borderRadius:16, padding:20 }}>
              <p style={{ fontSize:14, fontWeight:600, color:T.text, margin:'0 0 12px' }}>Upload Filled Template</p>
              {parseError && <p style={{ fontSize:12, color:'#ef4444', marginBottom:8, background: darkMode?'rgba(239,68,68,0.1)':'#fef2f2', padding:'8px 12px', borderRadius:8 }}>{parseError}</p>}
              <label style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', border:`2px dashed ${T.border2}`, borderRadius:12, padding:'32px 16px', cursor:'pointer', transition:'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor='#3b82f6'; e.currentTarget.style.background = darkMode?'rgba(59,130,246,0.06)':'#eff6ff'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor=T.border2; e.currentTarget.style.background='transparent'; }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <p style={{ fontSize:14, color:T.text2, marginTop:12, fontWeight:500 }}>Click to upload Excel file</p>
                <p style={{ fontSize:12, color:T.text3, marginTop:4 }}>.xlsx or .xls only</p>
                <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display:'none' }} onChange={handleFile} />
              </label>
            </div>
          </div>
        )}

        {/* Preview Stage */}
        {stage === 'preview' && (
          <div style={{ padding:24 }}>
            <div style={{ overflowX:'auto', borderRadius:12, border:`1px solid ${T.border}`, marginBottom:20 }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                  <tr>
                    {['Row','Service ID','Service Name','Category','Type','Rate (₹)','GST %','Status','⚠'].map(h => (
                      <th key={h} style={{ padding:'9px 12px', textAlign:'left', fontSize:10, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} style={{ borderBottom:`1px solid ${T.border}`, background: row._error?(darkMode?'rgba(239,68,68,0.08)':'#fef2f2'):(darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff')) }}>
                      <td style={{ padding:'8px 12px', fontSize:12, color:T.text3 }}>{row._row}</td>
                      <td style={{ padding:'8px 12px' }}>
                        {row.service_code
                          ? <span style={{ fontFamily:'monospace', fontWeight:700, color:'#3b82f6', background: darkMode?'rgba(59,130,246,0.1)':'#eff6ff', border:`1px solid ${darkMode?'rgba(59,130,246,0.2)':'#bfdbfe'}`, padding:'2px 6px', borderRadius:5, fontSize:11 }}>{row.service_code}</span>
                          : <span style={{ fontSize:12, color:T.text3 }}>—</span>}
                      </td>
                      <td style={{ padding:'8px 12px', fontSize:12, fontWeight:500, color:T.text, maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.name || <span style={{ color:'#ef4444', fontStyle:'italic' }}>missing</span>}</td>
                      <td style={{ padding:'8px 12px', fontSize:12, color:T.text2, whiteSpace:'nowrap' }}>{row.category}</td>
                      <td style={{ padding:'8px 12px', fontSize:12, color:T.text2 }}>{row.service_type === 'one_time' ? 'One Time' : 'Recurring'}</td>
                      <td style={{ padding:'8px 12px', fontSize:12, color:T.text2 }}>{row.default_amount ? `₹${Number(row.default_amount).toLocaleString('en-IN')}` : '-'}</td>
                      <td style={{ padding:'8px 12px', fontSize:12, color:T.text2 }}>{row.gst_percent}%</td>
                      <td style={{ padding:'8px 12px' }}>
                        <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:600, background: row.status==='active'?(darkMode?'rgba(16,185,129,0.1)':'#f0fdf4'):(darkMode?'rgba(100,116,139,0.1)':'#f8fafc'), color: row.status==='active'?'#16a34a':T.text3 }}>
                          {row.status}
                        </span>
                      </td>
                      <td style={{ padding:'8px 12px', fontSize:11, color:'#ef4444' }}>{row._error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ display:'flex', alignItems:'center', gap:16, fontSize:13 }}>
                <span style={{ color:'#16a34a', fontWeight:600, display:'flex', alignItems:'center', gap:5 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
                  {rows.filter(r => !r._error).length} valid
                </span>
                {rows.some(r => r._error) && (
                  <span style={{ color:'#ef4444', fontWeight:600, display:'flex', alignItems:'center', gap:5 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    {rows.filter(r => r._error).length} errors (skipped)
                  </span>
                )}
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button onClick={() => { setStage('upload'); setRows([]); if(fileInputRef.current) fileInputRef.current.value=''; }}
                  style={{ padding:'9px 18px', borderRadius:9, border:`1.5px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer' }}>
                  ← Re-upload
                </button>
                <button onClick={handleImport} disabled={importing || !rows.filter(r => !r._error).length}
                  style={{ padding:'9px 20px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:8, opacity: (importing||!rows.filter(r=>!r._error).length)?0.5:1 }}>
                  {importing
                    ? <><span style={{ width:13, height:13, border:'2px solid rgba(255,255,255,0.4)', borderTopColor:'#fff', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite' }}/>Importing...</>
                    : `✓ Import ${rows.filter(r => !r._error).length} Services`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════
// TEMPLATES TAB
// ════════════════════════════════════════════════════════════════
function TemplatesTab({ token, darkMode, T, showConfirm, newTick = 0, onCount }) {
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const { showToast } = useToast();
  const { canCreate, canEdit, canDelete, deny } = useServiceAccess(showToast);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);

  const fetchTemplates = async () => {
    try {
      const r = await fetch(`${BASE}/api/retainers/templates`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setTemplates(Array.isArray(d) ? d : []);
      onCount?.(Array.isArray(d) ? d.length : 0);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const handleDelete = (t) => {
    if (!canDelete) return deny('delete');
    showConfirm('Delete Template', `Delete template "${t.name}"?`, async () => {
      await fetch(`${BASE}/api/retainers/templates/${t.id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      fetchTemplates();
    });
  };

  // "New Template" button lives in the shared tab bar (ServicesBody); it bumps newTick.
  useEffect(() => {
    if (!newTick) return;
    if (!canCreate) { deny('create'); return; }
    setEditTemplate(null); setShowModal(true);
  }, [newTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const FREQ_LABELS = { monthly:'Monthly', quarterly:'Quarterly', half_yearly:'Half Yearly', yearly:'Yearly', one_time:'One Time', every_n_months:'Custom' };

  return (
    <div style={{ flex:1, minHeight:0, display:'flex', flexDirection:'column', gap:10 }}>
      {/* Templates Grid */}
      <div style={{ flex:1, minHeight:0, overflowY:'auto', display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:14, alignContent:'start' }}>
        {loading ? (
          <div style={{ gridColumn:'1/-1', textAlign:'center', padding:40, color:T.text3 }}>Loading...</div>
        ) : templates.length === 0 ? (
          <div style={{ gridColumn:'1/-1', textAlign:'center', padding:60, color:T.text3 }}>
            <div style={{ fontSize:32, marginBottom:12 }}>📋</div>
            <p style={{ fontSize:14, fontWeight:600, color:T.text2, margin:'0 0 6px' }}>No templates yet</p>
            <p style={{ fontSize:12, color:T.text3, margin:0 }}>Create reusable retainer templates for your common service packages.</p>
          </div>
        ) : templates.map(t => (
          <div key={t.id} style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:20, boxShadow: darkMode?'0 2px 12px rgba(0,0,0,0.2)':'0 1px 6px rgba(15,31,69,0.06)' }}>
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:12 }}>
              <div style={{ flex:1 }}>
                <p style={{ fontSize:14, fontWeight:700, color:T.text, margin:'0 0 4px' }}>{t.name}</p>
                {t.description && <p style={{ fontSize:12, color:T.text3, margin:0 }}>{t.description}</p>}
              </div>
              <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20, background: t.is_active?(darkMode?'rgba(16,185,129,0.12)':'#f0fdf4'):(darkMode?'rgba(100,116,139,0.12)':'#f8fafc'), color: t.is_active?'#16a34a':T.text3, marginLeft:8, flexShrink:0 }}>
                {t.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:14 }}>
              {[
                { label:'Default Amount', value: t.default_amount ? `₹${Number(t.default_amount).toLocaleString('en-IN')}` : '—' },
                { label:'Frequency', value: FREQ_LABELS[t.billing_frequency] || t.billing_frequency },
                { label:'Billing Mode', value: t.billing_mode === 'prepaid' ? 'Prepaid' : 'Postpaid' },
                { label:'GST Rate', value: `${t.gst_rate == null || t.gst_rate === '' ? 18 : Number(t.gst_rate)}%` },
              ].map(row => (
                <div key={row.label}>
                  <p style={{ fontSize:10, color:T.text3, margin:'0 0 2px', textTransform:'uppercase', letterSpacing:'0.05em' }}>{row.label}</p>
                  <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>{row.value}</p>
                </div>
              ))}
            </div>
            {(canEdit || canDelete) && <div style={{ display:'flex', gap:8 }}>
              {canEdit && <button onClick={() => { setEditTemplate(t); setShowModal(true); }}
                style={{ flex:1, padding:'7px 0', borderRadius:8, border:`1px solid ${T.border2}`, background:T.bg3, color:T.text2, fontSize:12, fontWeight:500, cursor:'pointer' }}>
                Edit
              </button>}
              {canDelete && <button onClick={() => handleDelete(t)}
                style={{ flex:1, padding:'7px 0', borderRadius:8, border:'1px solid #fecaca', background:darkMode?'rgba(239,68,68,0.08)':'#fef2f2', color:'#ef4444', fontSize:12, fontWeight:500, cursor:'pointer' }}>
                Delete
              </button>}
            </div>}
          </div>
        ))}
      </div>

      {showModal && (editTemplate ? canEdit : canCreate) && (
        <TemplateModal
          token={token} template={editTemplate}
          onClose={() => setShowModal(false)}
          onSuccess={() => { fetchTemplates(); setShowModal(false); }}
          darkMode={darkMode} T={T} />
      )}
    </div>
  );
}

function TemplateModal({ token, template, onClose, onSuccess, darkMode, T }) {
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const { showToast } = useToast();
  const { canCreate, canEdit, deny } = useServiceAccess(showToast);
  const [toast, setToast] = useState(null);
  const [allServices, setAllServices] = useState([]);
  const [serviceSearch, setServiceSearch] = useState('');
  const [selectedServices, setSelectedServices] = useState(() => {
    try { return Array.isArray(template?.service_ids) ? template.service_ids : JSON.parse(template?.service_ids || '[]'); }
    catch { return []; }
  });
  const [form, setForm] = useState({
    name: template?.name || '',
    description: template?.description || '',
    default_amount: template?.default_amount || '',
    billing_frequency: template?.billing_frequency || 'monthly',
    frequency_months: template?.frequency_months || 1,
    billing_mode: template?.billing_mode || 'postpaid',
    sac_code: template?.sac_code || '',
    gst_rate: template?.gst_rate == null || template?.gst_rate === '' ? 18 : Number(template.gst_rate),
    is_active: template?.is_active !== false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${BASE}/api/services/all`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setAllServices(Array.isArray(d) ? d.filter(s => s.status === 'active') : []))
      .catch(console.error);
  }, []);

  const toggleService = (svc) => {
    setSelectedServices(prev => {
      const exists = prev.find(s => s.id === svc.id);
      if (exists) return prev.filter(s => s.id !== svc.id);
      return [...prev, { id: svc.id, name: svc.name, category: svc.category, default_amount: svc.default_amount, sac_code: svc.sac_code }];
    });
  };

  const totalAmount = selectedServices.reduce((sum, s) => sum + Number(s.default_amount || 0), 0);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };

  const filteredServices = allServices.filter(s =>
    !serviceSearch || s.name?.toLowerCase().includes(serviceSearch.toLowerCase()) || s.category?.toLowerCase().includes(serviceSearch.toLowerCase())
  );

  const handleSave = async () => {
    if (template ? !canEdit : !canCreate) return deny(template ? 'edit' : 'create');
    if (!form.name) { setToast({ msg: 'Template name is required', type: 'error' }); return; }
    if (selectedServices.length === 0) { setToast({ msg: 'Select at least one service to bundle in this template.', type: 'error' }); return; }
    setSaving(true);
    try {
      const url = template ? `${BASE}/api/retainers/templates/${template.id}` : `${BASE}/api/retainers/templates`;
      const res = await fetch(url, {
        method: template ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...form,
          service_ids: selectedServices,
          default_amount: Number(form.default_amount) || totalAmount || 0,
        })
      });
      if (res.ok) { onSuccess(); return; }
      const d = await res.json();
      setToast({ msg: d.message || 'Save failed', type: 'error' });
    } catch(e) {
      console.error('Template save error:', e);
      setToast({ msg: 'Server error. Check backend is running.', type: 'error' });
    }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:99999, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:16, width:'min(95vw,640px)', maxHeight:'92vh', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>

        {/* Header */}
        <div style={{ padding:'20px 24px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <h3 style={{ fontSize:16, fontWeight:700, color:T.text, margin:'0 0 3px' }}>{template ? 'Edit Template' : 'New Retainer Template'}</h3>
            <p style={{ fontSize:12, color:T.text3, margin:0 }}>Bundle services into a reusable retainer package</p>
          </div>
          <button onClick={onClose} style={{ width:28, height:28, borderRadius:8, border:'none', background:T.bg3, color:T.text2, cursor:'pointer', fontSize:16 }}>×</button>
        </div>

        {/* Toast */}
        {toast && (
          <div style={{ margin:'12px 24px 0', padding:'10px 16px', borderRadius:10, fontSize:13, fontWeight:500,
            background: toast.type==='error'?(darkMode?'rgba(239,68,68,0.15)':'#fef2f2'):(darkMode?'rgba(22,163,74,0.15)':'#f0fdf4'),
            color: toast.type==='error'?'#ef4444':'#16a34a',
            border: `1px solid ${toast.type==='error'?(darkMode?'rgba(239,68,68,0.3)':'#fecaca'):(darkMode?'rgba(22,163,74,0.3)':'#bbf7d0')}`,
            display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <span>{toast.msg}</span>
            <button onClick={() => setToast(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'inherit', fontSize:16, lineHeight:1 }}>×</button>
          </div>
        )}

        <style>{`.tpl-scroll::-webkit-scrollbar{display:none}`}</style>
        <div className="tpl-scroll" style={{ flex:1, overflowY:'auto', padding:24, display:'flex', flexDirection:'column', gap:16, scrollbarWidth:'none', msOverflowStyle:'none' }}>

          {/* Template Name + Description */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div style={{ gridColumn:'1/-1' }}>
              <label style={{ fontSize:12, fontWeight:600, color:T.text3, display:'block', marginBottom:6 }}>Template Name *</label>
              <input value={form.name} onChange={set('name')} placeholder="e.g. GST Filing Annual Compliance - Monthly" style={is} />
            </div>
            <div style={{ gridColumn:'1/-1' }}>
              <label style={{ fontSize:12, fontWeight:600, color:T.text3, display:'block', marginBottom:6 }}>Description</label>
              <input value={form.description} onChange={set('description')} placeholder="What does this retainer package include?" style={is} />
            </div>
          </div>

          {/* SERVICE BUNDLE PICKER — the core feature */}
          <div style={{ border:`1.5px solid ${selectedServices.length > 0 ? '#2563eb' : T.border2}`, borderRadius:12, overflow:'visible', transition:'border-color 0.2s' }}>
            <div style={{ padding:'12px 16px', background: selectedServices.length > 0 ? (darkMode?'rgba(37,99,235,0.1)':'#eff6ff') : T.bg3, borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <p style={{ fontSize:13, fontWeight:700, color: selectedServices.length > 0 ? '#2563eb' : T.text, margin:'0 0 2px' }}>
                  Included Services *
                </p>
                <p style={{ fontSize:11, color:T.text3, margin:0 }}>
                  {selectedServices.length === 0 ? 'Search and select services to bundle' : `${selectedServices.length} service(s) selected — Total: ₹${totalAmount.toLocaleString('en-IN')}`}
                </p>
              </div>
              {selectedServices.length > 0 && (
                <button onClick={() => setSelectedServices([])}
                  style={{ fontSize:11, color:'#ef4444', background:'none', border:'none', cursor:'pointer', fontWeight:500 }}>
                  Clear all
                </button>
              )}
            </div>

            {/* Selected services chips */}
            {selectedServices.length > 0 && (
              <div style={{ padding:'10px 14px', display:'flex', flexWrap:'wrap', gap:8, borderBottom:`1px solid ${T.border}`, background:T.bg2 }}>
                {selectedServices.map(s => (
                  <div key={s.id} style={{ display:'flex', alignItems:'center', gap:6, padding:'4px 10px', borderRadius:20, background: darkMode?'rgba(37,99,235,0.15)':'#dbeafe', border:'1px solid #93c5fd' }}>
                    <span style={{ fontSize:12, fontWeight:500, color:'#2563eb' }}>{s.name}</span>
                    {s.default_amount > 0 && <span style={{ fontSize:11, color:'#60a5fa' }}>₹{Number(s.default_amount).toLocaleString('en-IN')}</span>}
                    <button onClick={() => toggleService(s)}
                      style={{ width:16, height:16, borderRadius:'50%', border:'none', background:'#93c5fd', color:'#1e40af', cursor:'pointer', fontSize:12, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, lineHeight:1, padding:0 }}>
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Search + list */}
            <div style={{ padding:'10px 14px', borderBottom:`1px solid ${T.border}`, background:T.bg3 }}>
              <input
                value={serviceSearch} onChange={e => setServiceSearch(e.target.value)}
                placeholder="Search services by name or category..."
                style={{ ...is, background:T.bg2, padding:'7px 12px' }} />
            </div>
            <div className="tpl-scroll" style={{ maxHeight:180, overflowY:'auto', scrollbarWidth:'none', msOverflowStyle:'none' }}>
              {filteredServices.length === 0 ? (
                <p style={{ textAlign:'center', padding:'20px 0', color:T.text3, fontSize:13 }}>
                  {allServices.length === 0 ? 'No active services found. Add services in Services Master first.' : 'No services match your search.'}
                </p>
              ) : filteredServices.map(svc => {
                const selected = selectedServices.some(s => s.id === svc.id);
                return (
                  <div key={svc.id} onClick={() => toggleService(svc)}
                    style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 16px', cursor:'pointer', borderBottom:`1px solid ${T.border}`, background: selected?(darkMode?'rgba(37,99,235,0.08)':'#f0f6ff'):T.bg2, transition:'background 0.1s' }}
                    onMouseEnter={e => { if (!selected) e.currentTarget.style.background = darkMode?'rgba(255,255,255,0.04)':'#f8faff'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = selected?(darkMode?'rgba(37,99,235,0.08)':'#f0f6ff'):T.bg2; }}>
                    {/* Checkbox */}
                    <div style={{ width:18, height:18, borderRadius:5, border:`2px solid ${selected?'#2563eb':T.border2}`, background:selected?'#2563eb':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      {selected && <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </div>
                    {/* Service info */}
                    <div style={{ flex:1, minWidth:0 }}>
                      <p style={{ fontSize:13, fontWeight:500, color:T.text, margin:'0 0 2px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{svc.name}</p>
                      <p style={{ fontSize:11, color:T.text3, margin:0 }}>{svc.category} {svc.service_code ? `· ${svc.service_code}` : ''}</p>
                    </div>
                    {/* Amount */}
                    {svc.default_amount > 0 && (
                      <span style={{ fontSize:12, fontWeight:600, color:'#16a34a', flexShrink:0 }}>₹{Number(svc.default_amount).toLocaleString('en-IN')}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Billing settings */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:T.text3, display:'block', marginBottom:6 }}>Default Amount (₹)</label>
              <input type="number" value={form.default_amount || totalAmount || ''} onChange={set('default_amount')} placeholder={totalAmount > 0 ? `Auto: ₹${totalAmount.toLocaleString('en-IN')}` : '0'} style={is} />
              {totalAmount > 0 && !form.default_amount && <p style={{ fontSize:11, color:'#16a34a', margin:'4px 0 0' }}>Auto-calculated from selected services</p>}
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:T.text3, display:'block', marginBottom:6 }}>GST Rate (%)</label>
              <select value={form.gst_rate} onChange={set('gst_rate')} style={is}>
                <option value={0}>0% (Exempt)</option>
                <option value={5}>5%</option>
                <option value={12}>12%</option>
                <option value={18}>18%</option>
                <option value={28}>28%</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:T.text3, display:'block', marginBottom:6 }}>Billing Frequency</label>
              <select value={form.billing_frequency} onChange={e => setForm(f => ({ ...f, billing_frequency: e.target.value }))} style={is}>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="half_yearly">Half Yearly</option>
                <option value="yearly">Yearly</option>
                <option value="one_time">One Time</option>
                <option value="every_n_months">Every N Months</option>
              </select>
            </div>
            {form.billing_frequency === 'every_n_months' ? (
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:T.text3, display:'block', marginBottom:6 }}>Every (months)</label>
                <input type="number" min="1" max="12" value={form.frequency_months} onChange={set('frequency_months')} style={is} />
              </div>
            ) : (
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:T.text3, display:'block', marginBottom:6 }}>Billing Mode</label>
                <select value={form.billing_mode} onChange={set('billing_mode')} style={is}>
                  <option value="postpaid">Postpaid (bill after service)</option>
                  <option value="prepaid">Prepaid (bill before service)</option>
                </select>
              </div>
            )}
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:T.text3, display:'block', marginBottom:6 }}>SAC Code (for Invoice)</label>
              <input value={form.sac_code} onChange={set('sac_code')} placeholder="e.g. 998231" style={is} />
            </div>
          </div>

          <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
            <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
            <span style={{ fontSize:13, color:T.text2 }}>Active template (visible when creating retainers)</span>
          </label>
        </div>

        {/* Footer */}
        <div style={{ padding:'16px 24px', borderTop:`1px solid ${T.border}`, display:'flex', gap:10, alignItems:'center' }}>
          {selectedServices.length > 0 && (
            <p style={{ fontSize:12, color:T.text3, margin:0, flex:1 }}>
              {selectedServices.length} service(s) · ₹{totalAmount.toLocaleString('en-IN')} bundled
            </p>
          )}
          <button onClick={onClose} style={{ padding:'10px 20px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg3, color:T.text2, fontSize:13, fontWeight:600, cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding:'10px 24px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:saving?0.7:1 }}>
            {saving ? 'Saving...' : template ? 'Update Template' : 'Create Template'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// RETAINERS TAB
// ════════════════════════════════════════════════════════════════
function RetainersTab({ token, darkMode, T, showConfirm, newTick = 0, onCount }) {
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const { showToast } = useToast();
  const { canCreate, canEdit, canDelete, canIn, deny } = useServiceAccess(showToast);
  // "Generate Invoice" creates a Finance invoice, so it needs finance.create on top of
  // services.edit (it updates the retainer's billing state).
  const canGenerateInvoice = canEdit && canIn('finance', 'create');
  const [retainers, setRetainers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('ongoing');
  const [showModal, setShowModal] = useState(false);
  const [editRetainer, setEditRetainer] = useState(null);
  const [viewRetainer, setViewRetainer] = useState(null);
  const [retainerToast, setRetainerToast] = useState(null);
  const [generatingInvoiceId, setGeneratingInvoiceId] = useState(null);

  const fetchAll = async () => {
    try {
      const h = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams({ status: filterStatus });
      if (search) params.append('search', search);
      const [rRes, sRes] = await Promise.all([
        fetch(`${BASE}/api/retainers?${params}`, { headers: h }),
        fetch(`${BASE}/api/retainers/stats`, { headers: h }),
      ]);
      const rData = await rRes.json();
      const sData = await sRes.json();
      setRetainers(Array.isArray(rData) ? rData : []);
      setStats(sData || {});
      onCount?.(Number(sData?.ongoing) || 0);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [filterStatus, search]);

  const handleCancel = (r) => {
    if (!canDelete) return deny('delete');
    showConfirm('Cancel Retainer', `Cancel retainer "${r.retainer_name}" for ${r.client_name}?`, async () => {
      await fetch(`${BASE}/api/retainers/${r.id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      fetchAll();
    }, 'Cancel Retainer', '#f97316');
  };

  const handleGenerateInvoice = async (r) => {
    if (generatingInvoiceId) return; // guard against double-click firing two requests at once
    if (!canEdit) return deny('edit');
    if (!canIn('finance', 'create')) {
      showToast("You don't have permission to create invoices. Ask your admin.", 'error');
      return;
    }
    setGeneratingInvoiceId(r.id);
    try {
      const res = await fetch(`${BASE}/api/retainers/${r.id}/invoice`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const d = await res.json();
      if (res.ok) {
        setRetainerToast({ msg: `Invoice ${d.invoice_number} created as draft. Go to Finance → Invoices to view it.`, type:'success' });
        fetchAll();
      } else {
        setRetainerToast({ msg: d.message || 'Failed to generate invoice', type:'error' });
      }
    } catch(e) { setRetainerToast({ msg: 'Server error', type:'error' }); }
    finally { setGeneratingInvoiceId(null); }
  };

  // "New Retainer" button lives in the shared tab bar (ServicesBody); it bumps newTick.
  useEffect(() => {
    if (!newTick) return;
    if (!canCreate) { deny('create'); return; }
    setEditRetainer(null); setShowModal(true);
  }, [newTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const FREQ = { monthly:'Monthly', quarterly:'Quarterly', half_yearly:'Half Yearly', yearly:'Yearly', one_time:'One Time', every_n_months:'Custom' };

  const fc = (n) => `₹${Number(n||0).toLocaleString('en-IN')}`;

  return (
    <div style={{ flex:1, minHeight:0, display:'flex', flexDirection:'column', gap:10 }}>

      {/* Toast */}
      {retainerToast && (
        <div style={{ padding:'10px 16px', borderRadius:10, fontSize:13, fontWeight:500, marginBottom:4,
          background: retainerToast.type==='error'?'#fef2f2':'#f0fdf4',
          color: retainerToast.type==='error'?'#ef4444':'#16a34a',
          border: `1px solid ${retainerToast.type==='error'?'#fecaca':'#bbf7d0'}`,
          display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span>{retainerToast.msg}</span>
          <button onClick={() => setRetainerToast(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'inherit', fontSize:16 }}>×</button>
        </div>
      )}

      {/* Stats row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
        {[
          { label:'Active Retainers', value: stats.ongoing || 0, color:'#2563eb' },
          { label:'Completed', value: stats.completed || 0, color:'#16a34a' },
          { label:'Cancelled', value: stats.cancelled || 0, color:'#94a3b8' },
          { label:'Total ARR', value: fc(stats.total_arr), color:'#f97316' },
        ].map(s => (
          <div key={s.label} style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:12, padding:'14px 18px', boxShadow: darkMode?'0 2px 8px rgba(0,0,0,0.2)':'0 1px 4px rgba(15,31,69,0.05)' }}>
            <p style={{ fontSize:10, color:T.text3, margin:'0 0 6px', textTransform:'uppercase', letterSpacing:'0.08em' }}>{s.label}</p>
            <p style={{ fontSize:22, fontWeight:800, color:s.color, margin:0 }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Search + filter bar (same card as Services / Tasks) */}
      <div style={{ background:T.bg2, borderRadius:12, border:`1px solid ${T.cardBorder}`, padding:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search client or retainer name..."
            style={{ flex:1, minWidth:0, border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'9px 14px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }} />
          <div style={{ width:160, flexShrink:0 }}>
            <SvcSelect value={filterStatus || 'all'} onChange={v => setFilterStatus(v === 'all' ? '' : v)} T={T} darkMode={darkMode}
              options={[{ value:'all', label:'All Status' }, { value:'ongoing', label:'Ongoing' }, { value:'completed', label:'Completed' }, { value:'cancelled', label:'Cancelled' }]} />
          </div>
          <p style={{ fontSize:12, color:T.text3, whiteSpace:'nowrap', margin:0 }}>{retainers.length} found</p>
        </div>
      </div>

      {/* Retainers List */}
      <div style={{ flex:1, minHeight:0, background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, overflow:'hidden', display:'flex', flexDirection:'column', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
        <div style={{ flex:1, overflowY:'auto', scrollbarWidth:'none', msOverflowStyle:'none' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', position:'sticky', top:0, zIndex:10 }}>
              <tr>
                {['Retainer', 'Client', 'Period', 'Billing Frequency', 'Amount', 'Billed', 'Billing Status', ''].map(h => (
                  <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ padding:40, textAlign:'center', color:T.text3 }}>Loading...</td></tr>
              ) : retainers.length === 0 ? (
                <tr><td colSpan={8} style={{ padding:60, textAlign:'center', color:T.text3 }}>
                  <div style={{ fontSize:28, marginBottom:10 }}>📄</div>
                  <p style={{ fontSize:14, fontWeight:600, color:T.text2, margin:'0 0 4px' }}>No retainers found</p>
                  <p style={{ fontSize:12, margin:0 }}>Click "+ New Retainer" to create your first retainer.</p>
                </td></tr>
              ) : retainers.map((r, idx) => {
                const billed = Number(r.total_billed || 0);
                const total = Number(r.amount || 0);
                const pct = total > 0 ? Math.min(100, (billed / total) * 100) : 0;
                const isUpToDate = r.billing_status === 'up_to_date';

                return (
                  <tr key={r.id} style={{ borderBottom:`1px solid ${T.border}`, background: darkMode?(idx%2===0?T.bg2:T.bg3):(idx%2===0?'#fff':'#fafbff'), transition:'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = darkMode?'#1e3a5f':'#f0f4ff'}
                    onMouseLeave={e => e.currentTarget.style.background = darkMode?(idx%2===0?T.bg2:T.bg3):(idx%2===0?'#fff':'#fafbff')}>

                    {/* Retainer name + period badge */}
                    <td style={{ padding:'12px 16px', maxWidth:220 }}>
                      <button onClick={() => setViewRetainer(r)} style={{ background:'none', border:'none', cursor:'pointer', textAlign:'left', padding:0 }}>
                        <p style={{ fontSize:13, fontWeight:600, color: darkMode?'#93c5fd':'#2563eb', margin:'0 0 3px' }}
                          onMouseEnter={e => e.target.style.textDecoration='underline'}
                          onMouseLeave={e => e.target.style.textDecoration='none'}>
                          {r.retainer_name}
                        </p>
                      </button>
                      <span style={{ fontSize:10, fontWeight:600, padding:'2px 8px', borderRadius:12,
                        background: r.status==='ongoing'?(darkMode?'rgba(22,163,74,0.12)':'#f0fdf4'):(darkMode?'rgba(100,116,139,0.12)':'#f8fafc'),
                        color: r.status==='ongoing'?(darkMode?'#4ade80':'#16a34a'):T.text3 }}>
                        {r.status?.toUpperCase()}
                      </span>
                    </td>

                    {/* Client */}
                    <td style={{ padding:'12px 16px', fontSize:13, color:T.text2 }}>
                      <p style={{ margin:'0 0 2px', fontWeight:500 }}>{r.client_name}</p>
                      {r.file_number && <p style={{ margin:0, fontSize:11, color:T.text3 }}>{r.file_number}</p>}
                    </td>

                    {/* Period */}
                    <td style={{ padding:'12px 16px', fontSize:12, color:T.text3, whiteSpace:'nowrap' }}>
                      {r.period_start && r.period_end ? (
                        <>
                          <p style={{ margin:'0 0 2px', fontSize:11 }}>
                            {new Date(r.period_start).toLocaleDateString('en-IN',{month:'short',year:'numeric'})} —
                          </p>
                          <p style={{ margin:0, fontSize:11 }}>
                            {new Date(r.period_end).toLocaleDateString('en-IN',{month:'short',year:'numeric'})}
                          </p>
                        </>
                      ) : '—'}
                    </td>

                    {/* Billing frequency */}
                    <td style={{ padding:'12px 16px', fontSize:12, color:T.text2 }}>
                      {FREQ[r.billing_frequency] || r.billing_frequency}
                      {r.billing_frequency === 'every_n_months' && ` (${r.frequency_months}m)`}
                      {r.total_cycles > 0 && <p style={{ margin:'2px 0 0', fontSize:11, color:T.text3 }}>{r.total_cycles} cycles</p>}
                    </td>

                    {/* Total amount + per invoice */}
                    <td style={{ padding:'12px 16px', fontSize:13, fontWeight:600, color:T.text }}>
                      {fc(r.amount)}
                      {r.amount_per_invoice > 0 && Number(r.amount_per_invoice) !== Number(r.amount) && (
                        <p style={{ margin:'2px 0 0', fontSize:11, color:T.text3 }}>
                          {fc(r.amount_per_invoice)}/cycle
                        </p>
                      )}
                    </td>

                    {/* Progress bar + billed amount */}
                    <td style={{ padding:'12px 16px', minWidth:160 }}>
                      <p style={{ fontSize:12, fontWeight:600, color:T.text, margin:'0 0 6px' }}>
                        {fc(billed)} / {fc(total)}
                      </p>
                      <div style={{ height:6, borderRadius:3, background: darkMode?'rgba(255,255,255,0.08)':'#e2e8f0', overflow:'hidden' }}>
                        <div style={{ height:'100%', width:`${pct}%`, borderRadius:3, background: pct>=100?'#16a34a':'linear-gradient(90deg,#2563eb,#3b82f6)', transition:'width 0.4s' }} />
                      </div>
                    </td>

                    {/* Billing status badge */}
                    <td style={{ padding:'12px 16px' }}>
                      <span style={{ fontSize:11, fontWeight:700, padding:'4px 10px', borderRadius:20,
                        background: isUpToDate?(darkMode?'rgba(22,163,74,0.12)':'#f0fdf4'):(darkMode?'rgba(249,115,22,0.12)':'#fff7ed'),
                        color: isUpToDate?'#16a34a':'#f97316',
                        border: `1px solid ${isUpToDate?(darkMode?'rgba(22,163,74,0.25)':'#bbf7d0'):(darkMode?'rgba(249,115,22,0.25)':'#fed7aa')}` }}>
                        {isUpToDate ? 'Up to Date' : 'Billing Due'}
                      </span>
                    </td>

                    {/* Actions kebab */}
                    <td style={{ padding:'12px 16px' }}>
                      <div style={{ display:'flex', gap:6 }}>
                        {canGenerateInvoice && r.status === 'ongoing' && r.billing_frequency !== 'non_billable' && (() => {
                          // Fully billed once every cycle in the schedule already has an
                          // invoice — the exact same check the backend's over-invoice guard
                          // uses, so this greys out in lockstep with when the click would
                          // otherwise be rejected with a 400.
                          const fullyBilled = Number(r.total_cycles) > 0 && Number(r.invoices_count) >= Number(r.total_cycles);
                          const isDisabled = fullyBilled || generatingInvoiceId === r.id;
                          return (
                            <button onClick={() => !fullyBilled && handleGenerateInvoice(r)} disabled={isDisabled}
                              title={fullyBilled ? 'Fully billed — every billing cycle already has an invoice' : 'Generate Next Invoice'}
                              style={{ padding:'5px 10px', borderRadius:7,
                                border:`1px solid ${fullyBilled ? T.border2 : darkMode?'rgba(22,163,74,0.3)':'#bbf7d0'}`,
                                background: fullyBilled ? T.bg3 : (darkMode?'rgba(22,163,74,0.08)':'#f0fdf4'),
                                color: fullyBilled ? T.text3 : '#16a34a',
                                fontSize:11, fontWeight:600, cursor: isDisabled ? 'not-allowed' : 'pointer',
                                opacity: isDisabled ? 0.55 : 1, whiteSpace:'nowrap' }}>
                              {generatingInvoiceId === r.id ? 'Generating...' : fullyBilled ? 'Fully Billed' : '+ Invoice'}
                            </button>
                          );
                        })()}
                        {canEdit && <button onClick={() => { setEditRetainer(r); setShowModal(true); }}
                          style={{ padding:'5px 10px', borderRadius:7, border:`1px solid ${T.border2}`, background:T.bg3, color:T.text2, fontSize:11, fontWeight:500, cursor:'pointer' }}>
                          Edit
                        </button>}
                        {canDelete && r.status === 'ongoing' && (
                          <button onClick={() => handleCancel(r)}
                            style={{ padding:'5px 10px', borderRadius:7, border:'1px solid #fed7aa', background:darkMode?'rgba(249,115,22,0.08)':'#fff7ed', color:'#f97316', fontSize:11, fontWeight:500, cursor:'pointer' }}>
                            Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* New/Edit Retainer Modal */}
      {showModal && (editRetainer ? canEdit : canCreate) && (
        <RetainerModal
          token={token} retainer={editRetainer}
          onClose={() => { setShowModal(false); setEditRetainer(null); }}
          onSuccess={() => { fetchAll(); setShowModal(false); setEditRetainer(null); }}
          darkMode={darkMode} T={T} />
      )}

      {/* View Retainer Detail */}
      {viewRetainer && (
        <RetainerDetailModal
          token={token} retainer={viewRetainer}
          onClose={() => setViewRetainer(null)}
          onGenerateInvoice={() => { handleGenerateInvoice(viewRetainer); setViewRetainer(null); }}
          canGenerateInvoice={canGenerateInvoice}
          darkMode={darkMode} T={T} />
      )}
    </div>
  );
}

// ── Retainer Detail Modal ─────────────────────────────────────────────────────
function RetainerDetailModal({ token, retainer, onClose, onGenerateInvoice, canGenerateInvoice = false, darkMode, T }) {
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BASE}/api/retainers/${retainer.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setDetail(d); setLoading(false); }).catch(() => setLoading(false));
  }, [retainer.id]);

  const fc = (n) => `₹${Number(n||0).toLocaleString('en-IN', { minimumFractionDigits:2 })}`;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:99999, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:16, width:'min(95vw,640px)', maxHeight:'90vh', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding:'20px 24px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <h3 style={{ fontSize:16, fontWeight:700, color:T.text, margin:'0 0 3px' }}>{retainer.retainer_name}</h3>
            <p style={{ fontSize:12, color:T.text3, margin:0 }}>{retainer.client_name}</p>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            {canGenerateInvoice && retainer.status === 'ongoing' && (() => {
              // Same "fully billed" check as the list view — every cycle in the schedule
              // already has a non-cancelled invoice, matching the backend's over-invoice guard.
              const fullyBilled = !!detail && detail.schedule?.length > 0 &&
                (detail.invoices || []).filter(i => i.status !== 'cancelled').length >= detail.schedule.length;
              return (
                <button onClick={onGenerateInvoice} disabled={fullyBilled}
                  title={fullyBilled ? 'Fully billed — every billing cycle already has an invoice' : undefined}
                  style={{ padding:'7px 14px', borderRadius:8, border:'none',
                    background: fullyBilled ? T.bg3 : 'linear-gradient(135deg,#0f1f45,#1a3a6b)',
                    color: fullyBilled ? T.text3 : '#fff', fontSize:12, fontWeight:600,
                    cursor: fullyBilled ? 'not-allowed' : 'pointer', opacity: fullyBilled ? 0.7 : 1 }}>
                  {fullyBilled ? 'Fully Billed' : 'Generate Invoice'}
                </button>
              );
            })()}
            <button onClick={onClose} style={{ width:28, height:28, borderRadius:8, border:'none', background:T.bg3, color:T.text2, cursor:'pointer', fontSize:16 }}>×</button>
          </div>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:24, scrollbarWidth:'none', msOverflowStyle:'none' }}>
          {loading ? (
            <p style={{ textAlign:'center', color:T.text3 }}>Loading...</p>
          ) : !detail ? (
            <p style={{ textAlign:'center', color:'#ef4444' }}>Failed to load detail</p>
          ) : (
            <>
              {/* Billing Schedule */}
              <h4 style={{ fontSize:13, fontWeight:700, color:T.text, margin:'0 0 12px' }}>Billing Schedule</h4>
              {detail.schedule && detail.schedule.length > 0 ? (
                <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:20 }}>
                  {detail.schedule.map((cycle, i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', borderRadius:10, border:`1px solid ${cycle.invoice ? (darkMode?'rgba(22,163,74,0.25)':'#bbf7d0') : T.border2}`, background: cycle.invoice?(darkMode?'rgba(22,163,74,0.06)':'#f0fdf4'):T.bg3 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <span style={{ width:24, height:24, borderRadius:'50%', background: cycle.invoice?'#16a34a':'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, flexShrink:0 }}>{i+1}</span>
                        <div>
                          <p style={{ fontSize:12, fontWeight:600, color:T.text, margin:'0 0 2px' }}>Cycle {i+1} — {fc(cycle.amount)}</p>
                          <p style={{ fontSize:11, color:T.text3, margin:0 }}>{new Date(cycle.from).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})} to {new Date(cycle.to).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</p>
                        </div>
                      </div>
                      {cycle.invoice ? (
                        <span style={{ fontSize:11, fontWeight:600, color:'#16a34a', background:darkMode?'rgba(22,163,74,0.12)':'#dcfce7', padding:'3px 10px', borderRadius:20 }}>
                          {cycle.invoice.invoice_number}
                        </span>
                      ) : cycle.is_due ? (
                        <span style={{ fontSize:11, fontWeight:600, color:'#f97316', background:darkMode?'rgba(249,115,22,0.12)':'#fff7ed', padding:'3px 10px', borderRadius:20 }}>Due</span>
                      ) : (
                        <span style={{ fontSize:11, color:T.text3, padding:'3px 10px' }}>Upcoming</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize:13, color:T.text3, marginBottom:20 }}>No billing schedule available.</p>
              )}

              {/* Invoice History */}
              {detail.invoices && detail.invoices.length > 0 && (
                <>
                  <h4 style={{ fontSize:13, fontWeight:700, color:T.text, margin:'0 0 12px' }}>Invoice History</h4>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                    <thead>
                      <tr style={{ background: darkMode?'rgba(255,255,255,0.05)':'#f8fafc' }}>
                        {['Invoice No.', 'Date', 'Amount', 'Paid', 'Status'].map(h => (
                          <th key={h} style={{ padding:'8px 12px', textAlign:'left', color:T.text3, fontWeight:600, fontSize:11, textTransform:'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {detail.invoices.map(inv => (
                        <tr key={inv.id} style={{ borderBottom:`1px solid ${T.border}` }}>
                          <td style={{ padding:'8px 12px', color:'#2563eb', fontWeight:500 }}>{inv.invoice_number}</td>
                          <td style={{ padding:'8px 12px', color:T.text3 }}>{new Date(inv.invoice_date).toLocaleDateString('en-IN')}</td>
                          <td style={{ padding:'8px 12px', fontWeight:600, color:T.text }}>₹{Number(inv.total_amount).toLocaleString('en-IN')}</td>
                          <td style={{ padding:'8px 12px', color:'#16a34a' }}>₹{Number(inv.amount_paid).toLocaleString('en-IN')}</td>
                          <td style={{ padding:'8px 12px' }}>
                            <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20, textTransform:'capitalize',
                              background: inv.status==='paid'?'#f0fdf4':inv.status==='draft'?'#f8fafc':'#fffbeb',
                              color: inv.status==='paid'?'#16a34a':inv.status==='draft'?'#64748b':'#d97706' }}>
                              {inv.status.replace('_',' ')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── New/Edit Retainer Modal ───────────────────────────────────────────────────
function RetainerModal({ token, retainer, onClose, onSuccess, darkMode, T }) {
  const { showToast } = useToast();
  const { canCreate, canEdit, deny } = useServiceAccess(showToast);
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [form, setForm] = useState({
    client_id: retainer?.client_id ? String(retainer.client_id) : '',
    template_id: retainer?.template_id || '',
    retainer_name: retainer?.retainer_name || '',
    period_type: retainer?.period_type || 'financial_year',
    period_start: retainer?.period_start?.split('T')[0] || '',
    period_end: retainer?.period_end?.split('T')[0] || '',
    billing_frequency: retainer?.billing_frequency || 'monthly',
    frequency_months: retainer?.frequency_months || 1,
    amount: retainer?.amount || '',
    billing_mode: retainer?.billing_mode || 'postpaid',
    auto_invoice: retainer?.auto_invoice || false,
    auto_renew: retainer?.auto_renew || false,
    sac_code: retainer?.sac_code || '',
    gst_rate: retainer?.gst_rate == null || retainer?.gst_rate === '' ? 18 : Number(retainer.gst_rate),
  });

  useEffect(() => {
    const h = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${BASE}/api/clients`, { headers: h }).then(r => r.json()),
      fetch(`${BASE}/api/retainers/templates`, { headers: h }).then(r => r.json()),
    ]).then(([c, t]) => {
      const clientList = Array.isArray(c) ? c : (Array.isArray(c?.clients) ? c.clients : []);
      const templateList = Array.isArray(t) ? t : [];
      setClients(clientList);
      setTemplates(templateList.filter(x => x.is_active));
    }).catch(console.error);
  }, []);

  // Auto-set FY dates when period_type = financial_year
  useEffect(() => {
    if (form.period_type === 'financial_year' && !retainer) {
      const now = new Date();
      const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
      setForm(f => ({ ...f, period_start: `${year}-04-01`, period_end: `${year+1}-03-31` }));
    }
  }, [form.period_type]);

  // Apply template values when template selected
  const applyTemplate = (templateId) => {
    const t = templates.find(x => String(x.id) === String(templateId));
    if (!t) return;
    setForm(f => ({
      ...f,
      template_id: templateId,
      retainer_name: t.name,
      billing_frequency: t.billing_frequency,
      frequency_months: t.frequency_months || 1,
      amount: f.amount || t.default_amount,
      billing_mode: t.billing_mode,
      sac_code: t.sac_code || f.sac_code,
      gst_rate: t.gst_rate == null || t.gst_rate === '' ? 18 : Number(t.gst_rate),
    }));
  };

  // Calculate preview
  const freqMonths = form.billing_frequency === 'monthly' ? 1
    : form.billing_frequency === 'quarterly' ? 3
    : form.billing_frequency === 'half_yearly' ? 6
    : form.billing_frequency === 'yearly' ? 12
    : form.billing_frequency === 'one_time' ? 999
    : parseInt(form.frequency_months) || 1;

  let previewText = '';
  if (form.period_start && form.period_end && form.amount) {
    const start = new Date(form.period_start);
    const end   = new Date(form.period_end);
    const diffMonths = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
    const cycles = form.billing_frequency === 'one_time' ? 1 : Math.ceil(diffMonths / freqMonths);
    const perInv = cycles > 0 ? Math.round(Number(form.amount) / cycles * 100) / 100 : 0;
    previewText = `${cycles} Invoice(s) of ₹${perInv.toLocaleString('en-IN')} each`;
  }

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };

  const selectedClientName = clients.find(c => String(c.id) === String(form.client_id))?.client_name || '';
  const filteredClients = clients.filter(c => !clientSearch || c.client_name?.toLowerCase().includes(clientSearch.toLowerCase()));

  const handleSave = async () => {
    if (retainer ? !canEdit : !canCreate) return deny(retainer ? 'edit' : 'create');
    if (!form.client_id) return showToast('Client is required', 'error');
    if (!form.retainer_name) return showToast('Retainer name is required', 'error');
    if (!form.amount) return showToast('Amount is required', 'error');
    setSaving(true);
    try {
      const url = retainer ? `${BASE}/api/retainers/${retainer.id}` : `${BASE}/api/retainers`;
      const res = await fetch(url, {
        method: retainer ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, frequency_months: freqMonths })
      });
      if (res.ok) { showToast(retainer ? 'Retainer updated' : 'Retainer created', 'success'); onSuccess(); }
      else { const d = await res.json(); showToast(d.message || 'Save failed', 'error'); }
    } catch(e) { showToast('Server error', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:99999, padding:16 }}>
      <div style={{ background:T.bg2, borderRadius:16, width:'min(95vw,560px)', maxHeight:'92vh', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
        {/* Header */}
        <div style={{ padding:'20px 24px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <h3 style={{ fontSize:16, fontWeight:700, color:T.text, margin:0 }}>{retainer ? 'Edit Retainer' : 'New Retainer'}</h3>
          <button onClick={onClose} style={{ width:28, height:28, borderRadius:8, border:'none', background:T.bg3, color:T.text2, cursor:'pointer', fontSize:16 }}>×</button>
        </div>

        {/* Form */}
        <style>{`.no-scroll::-webkit-scrollbar{display:none}`}</style>
        <div className="no-scroll" style={{ flex:1, overflowY:'auto', padding:24, display:'flex', flexDirection:'column', gap:14, scrollbarWidth:'none', msOverflowStyle:'none' }}>
          {/* Client */}
          <div style={{ position:'relative' }}>
            <label style={{ fontSize:12, fontWeight:600, color:T.text3, display:'block', marginBottom:6 }}>Client *</label>
            <button type="button" onClick={() => { setClientSearch(''); setShowClientPicker(p => !p); }}
              style={{ ...is, textAlign:'left', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }}>
              <span style={{ color: selectedClientName ? T.text : T.text3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selectedClientName || '— Select Client —'}</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink:0, marginLeft:6, transform: showClientPicker?'rotate(180deg)':'rotate(0deg)', transition:'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            {showClientPicker && (
              <div style={{ position:'absolute', left:0, top:'calc(100% + 4px)', background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.15)', zIndex:60, width:'100%' }}
              onMouseLeave={() => setShowClientPicker(false)}>
                <div style={{ padding:10, borderBottom:`1px solid ${T.border}` }}>
                  <div style={{ position:'relative' }}>
                    <svg style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)' }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input type="text" autoFocus placeholder="Search clients..." value={clientSearch} onChange={e => setClientSearch(e.target.value)}
                      style={{ width:'100%', paddingLeft:28, paddingRight:10, paddingTop:8, paddingBottom:8, border:`1px solid ${T.inputBorder}`, borderRadius:8, fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' }} />
                  </div>
                </div>
                <div style={{ maxHeight:220, overflowY:'auto' }}>
                  {filteredClients.length === 0 ? (
                    <p style={{ textAlign:'center', fontSize:12, color:T.text3, padding:'16px 0' }}>No clients found</p>
                  ) : filteredClients.map(c => {
                    const sel = String(form.client_id) === String(c.id);
                    const inactive = c.is_active === false;
                    return (
                      <button key={c.id} type="button" disabled={inactive}
                        onClick={() => { if (inactive) return; setForm(f => ({ ...f, client_id: String(c.id) })); setShowClientPicker(false); }}
                        title={inactive ? 'Inactive client' : undefined}
                        style={{ width:'100%', textAlign:'left', padding:'9px 14px', border:'none', cursor: inactive?'not-allowed':'pointer', fontSize:13, fontWeight:500,
                          background: sel?(darkMode?'rgba(59,130,246,0.1)':'#eff6ff'):'transparent',
                          color: inactive?T.text3:(sel?(darkMode?'#93c5fd':'#2563eb'):T.text),
                          opacity: inactive?0.55:1, display:'flex', alignItems:'center', gap:8 }}
                        onMouseEnter={e => { if(!sel && !inactive) e.currentTarget.style.background=T.bg3; }}
                        onMouseLeave={e => { if(!sel && !inactive) e.currentTarget.style.background='transparent'; }}>
                        <span>{c.client_name}</span>
                        {inactive && (
                          <span style={{ fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:8, background: darkMode?'rgba(100,116,139,0.2)':'#f1f5f9', color:T.text3, flexShrink:0 }}>INACTIVE</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Select from template */}
          {templates.length > 0 && (
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:T.text3, display:'block', marginBottom:6 }}>Select from Template</label>
              <select value={form.template_id} onChange={e => applyTemplate(e.target.value)} style={is}>
                <option value="">— Select template (optional) —</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}

          {/* Retainer Name */}
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:T.text3, display:'block', marginBottom:6 }}>Retainer Name *</label>
            <input value={form.retainer_name} onChange={set('retainer_name')} placeholder="e.g. GST Filing Annual Compliance - Monthly" style={is} />
          </div>

          {/* Period Type */}
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:T.text3, display:'block', marginBottom:8 }}>Retainer Period *</label>
            <div style={{ display:'flex', gap:10, marginBottom:10 }}>
              {[{v:'financial_year',l:'Financial Year'},{v:'custom',l:'Custom Period'}].map(opt => (
                <label key={opt.v} style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer', fontSize:13, color:T.text2 }}>
                  <input type="radio" name="period_type" value={opt.v} checked={form.period_type===opt.v} onChange={set('period_type')} />
                  {opt.l}
                </label>
              ))}
            </div>
            {form.period_type === 'financial_year' ? (
              /* FY dropdown — same as Practive */
              <select value={form.period_start} onChange={e => {
                const yr = parseInt(e.target.value.split('-')[0]);
                setForm(f => ({ ...f, period_start:`${yr}-04-01`, period_end:`${yr+1}-03-31` }));
              }} style={is}>
                <option value="">— Select Financial Year —</option>
                {Array.from({ length:12 }, (_,i) => {
                  const yr = new Date().getFullYear() - 3 + i;
                  return <option key={yr} value={`${yr}-04-01`}>FY {yr}-{String(yr+1).slice(2)}</option>;
                })}
              </select>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div>
                  <label style={{ fontSize:11, color:T.text3, display:'block', marginBottom:4 }}>From</label>
                  <input type="date" value={form.period_start} onChange={set('period_start')} style={is} />
                </div>
                <div>
                  <label style={{ fontSize:11, color:T.text3, display:'block', marginBottom:4 }}>To</label>
                  <input type="date" value={form.period_end} onChange={set('period_end')} style={is} />
                </div>
              </div>
            )}
          </div>

          {/* Billing Frequency */}
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:T.text3, display:'block', marginBottom:8 }}>Billing Frequency</label>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
              {[
                {v:'one_time',l:'One Time'},{v:'monthly',l:'Monthly'},{v:'quarterly',l:'Quarterly'},
                {v:'half_yearly',l:'Half Yearly'},{v:'yearly',l:'Yearly'},{v:'every_n_months',l:'Every N Months'},
                {v:'non_billable',l:'Non-Billable'},
              ].map(opt => (
                <label key={opt.v} style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', padding:'6px 12px', borderRadius:8, border:`1.5px solid ${form.billing_frequency===opt.v?'#0f1f45':T.border2}`, background:form.billing_frequency===opt.v?(darkMode?'rgba(15,31,69,0.2)':'#f0f4ff'):T.bg3, fontSize:12, fontWeight:500, color:form.billing_frequency===opt.v?T.text:T.text2 }}>
                  <input type="radio" name="billing_frequency" value={opt.v} checked={form.billing_frequency===opt.v} onChange={set('billing_frequency')} style={{ display:'none' }} />
                  {opt.l}
                </label>
              ))}
            </div>
            {form.billing_frequency === 'every_n_months' && (
              <div style={{ marginTop:10 }}>
                <label style={{ fontSize:11, color:T.text3, display:'block', marginBottom:4 }}>Every (months)</label>
                <input type="number" min="1" max="12" value={form.frequency_months} onChange={set('frequency_months')} style={{ ...is, width:120 }} />
              </div>
            )}
          </div>

          {/* Amount + Billing Mode */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:T.text3, display:'block', marginBottom:6 }}>Total Retainer Amount (₹) *</label>
              <input type="number" value={form.amount} onChange={set('amount')} placeholder="0" style={is} />
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:T.text3, display:'block', marginBottom:6 }}>Billing Mode</label>
              <select value={form.billing_mode} onChange={set('billing_mode')} style={is}>
                <option value="postpaid">Postpaid</option>
                <option value="prepaid">Prepaid</option>
              </select>
            </div>
          </div>

          {/* Preview */}
          {previewText && (
            <div style={{ background: darkMode?'rgba(37,99,235,0.08)':'#eff6ff', border:`1px solid ${darkMode?'rgba(37,99,235,0.2)':'#bfdbfe'}`, borderRadius:10, padding:'10px 16px' }}>
              <p style={{ fontSize:13, fontWeight:600, color:'#2563eb', margin:0 }}>📊 {previewText}</p>
            </div>
          )}

          {/* SAC + GST */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:T.text3, display:'block', marginBottom:6 }}>SAC Code</label>
              <input value={form.sac_code} onChange={set('sac_code')} placeholder="e.g. 998231" style={is} />
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:T.text3, display:'block', marginBottom:6 }}>GST Rate (%)</label>
              <select value={form.gst_rate} onChange={set('gst_rate')} style={is}>
                <option value={0}>0% (Exempt)</option>
                <option value={5}>5%</option>
                <option value={12}>12%</option>
                <option value={18}>18%</option>
                <option value={28}>28%</option>
              </select>
            </div>
          </div>

          {/* Toggles */}
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {[
              { key:'auto_invoice', label:'Create invoice automatically when billing cycle is due' },
              { key:'auto_renew',   label:'Renew automatically at end of period' },
            ].map(opt => (
              <label key={opt.key} style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
                <input type="checkbox" checked={form[opt.key]} onChange={e => setForm(f => ({ ...f, [opt.key]: e.target.checked }))} />
                <span style={{ fontSize:13, color:T.text2 }}>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding:'16px 24px', borderTop:`1px solid ${T.border}`, display:'flex', gap:10 }}>
          <button onClick={onClose} style={{ flex:1, padding:'10px 0', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg3, color:T.text2, fontSize:13, fontWeight:600, cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving}
            style={{ flex:2, padding:'10px 0', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:saving?0.7:1 }}>
            {saving ? 'Saving...' : retainer ? 'Update Retainer' : 'Create Retainer'}
          </button>
        </div>
      </div>
    </div>
  );
}


export default Services;